import React, { useState, useRef, useEffect } from 'react';
import { useGyerekek } from '../GyerekekContext';

// ============ ANIMÁLT HÁTTÉR ============
function AnimaltHatter() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    const reszecskek = Array.from({length: 30}, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      r: Math.random() * 24 + 8,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      szin: ['#ff6b9d','#ffd93d','#6bcb77','#4d96ff','#ff6b35','#c77dff'][Math.floor(Math.random()*6)],
      alpha: Math.random() * 0.10 + 0.03,
    }));
    const rajzol = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      reszecskek.forEach(r => {
        r.x += r.vx; r.y += r.vy;
        if (r.x < -r.r) r.x = canvas.width + r.r;
        if (r.x > canvas.width + r.r) r.x = -r.r;
        if (r.y < -r.r) r.y = canvas.height + r.r;
        if (r.y > canvas.height + r.r) r.y = -r.r;
        ctx.beginPath(); ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
        ctx.fillStyle = r.szin;
        ctx.globalAlpha = r.alpha;
        ctx.fill();
        ctx.globalAlpha = 1;
      });
      animId = requestAnimationFrame(rajzol);
    };
    rajzol();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0}}/>;
}

// ============ KONFETTI ============
function KonfettiHatter() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.parentElement?.offsetWidth || 600;
    canvas.height = canvas.parentElement?.offsetHeight || 400;
    const ctx = canvas.getContext('2d');
    let animId;
    const reszecskek = Array.from({length: 150}, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * 200,
      r: Math.random() * 9 + 4,
      vx: (Math.random() - 0.5) * 5,
      vy: Math.random() * 5 + 2,
      szin: ['#ff6b9d','#ffd93d','#6bcb77','#4d96ff','#ff6b35','#c77dff','#ff3366','#00bcd4','#ffab00'][Math.floor(Math.random()*9)],
      forgat: Math.random() * Math.PI * 2,
      forgSeb: (Math.random() - 0.5) * 0.18,
      forma: Math.floor(Math.random() * 3),
    }));
    const rajzol = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      reszecskek.forEach(r => {
        r.x += r.vx; r.y += r.vy; r.forgat += r.forgSeb;
        if (r.y > canvas.height + 20) { r.y = -20; r.x = Math.random() * canvas.width; }
        ctx.save();
        ctx.translate(r.x, r.y);
        ctx.rotate(r.forgat);
        ctx.fillStyle = r.szin;
        ctx.globalAlpha = 0.9;
        if (r.forma === 0) { ctx.fillRect(-r.r/2, -r.r/2, r.r, r.r*1.5); }
        else if (r.forma === 1) { ctx.beginPath(); ctx.arc(0, 0, r.r/2, 0, Math.PI*2); ctx.fill(); }
        else { ctx.beginPath(); ctx.moveTo(0,-r.r/2); ctx.lineTo(r.r/2,r.r/2); ctx.lineTo(-r.r/2,r.r/2); ctx.closePath(); ctx.fill(); }
        ctx.restore();
        ctx.globalAlpha = 1;
      });
      animId = requestAnimationFrame(rajzol);
    };
    rajzol();
    return () => cancelAnimationFrame(animId);
  }, []);
  return <canvas ref={canvasRef} style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', borderRadius: '28px', zIndex: 0}}/>;
}

// Ha az érték "/"-lel kezdődik (pl. { kep: "/jatekok/bicikli.png", emoji: "🚲" }), valódi képet
// jelenít meg; egyébként simán kiírja emojiként. Így bármelyik emoji
// később lecserélhető valódi képre a data tömbökben, kód-módosítás nélkül.
function KepVagyEmoji({ ertek, meret = 44 }) {
  const [hiba, setHiba] = useState(false);

  // Objektum forma: { kep: '/jatekok/xyz.png', emoji: '📹' } — ha a kép
  // hiányzik vagy nem tölt be, automatikusan visszaesik az emojira.
  if (ertek && typeof ertek === 'object' && ertek.kep) {
    if (hiba) return <span style={{fontSize: meret}}>{ertek.emoji}</span>;
    return <img src={ertek.kep} alt="" style={{width: meret, height: meret, objectFit: 'contain', display: 'inline-block'}} onError={() => setHiba(true)} />;
  }
  // Régi, egyszerű string forma: emoji vagy kép-útvonal (fallback nélkül).
  if (typeof ertek === 'string' && ertek.startsWith('/')) {
    if (hiba) return null;
    return <img src={ertek} alt="" style={{width: meret, height: meret, objectFit: 'contain', display: 'inline-block'}} onError={() => setHiba(true)} />;
  }
  return <span style={{fontSize: meret}}>{ertek}</span>;
}

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
  .leckek-wrapper * { font-family: 'Nunito', sans-serif !important; }
  @keyframes felugrik { 0% { transform: translateY(20px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
  @keyframes pulzal { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
  @keyframes ugorj { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  @keyframes lapKiBal { 0% { transform: perspective(1800px) rotateY(0deg) scale(1); opacity: 1; filter: blur(0px) brightness(1); } 60% { filter: blur(2px) brightness(0.85); } 100% { transform: perspective(1800px) rotateY(-100deg) scale(0.8); opacity: 0; filter: blur(3px) brightness(0.7); } }
  @keyframes lapKiJobb { 0% { transform: perspective(1800px) rotateY(0deg) scale(1); opacity: 1; filter: blur(0px) brightness(1); } 60% { filter: blur(2px) brightness(0.85); } 100% { transform: perspective(1800px) rotateY(100deg) scale(0.8); opacity: 0; filter: blur(3px) brightness(0.7); } }
  @keyframes lapBe { 0% { transform: perspective(1800px) rotateY(100deg) scale(0.8); opacity: 0; filter: blur(3px); } 70% { filter: blur(0px); } 100% { transform: perspective(1800px) rotateY(0deg) scale(1); opacity: 1; filter: blur(0px); } }
  @keyframes csillog { 0%,100% { opacity: 0.7; } 50% { opacity: 1; } }
  .lap-ki-bal { animation: lapKiBal 0.45s cubic-bezier(0.55,0,1,0.45) forwards; }
  .lap-ki-jobb { animation: lapKiJobb 0.45s cubic-bezier(0.55,0,1,0.45) forwards; }
  .lap-be { animation: lapBe 0.5s cubic-bezier(0.16,1,0.3,1) forwards; }
  .lk-btn { transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s ease !important; }
  .lk-btn:hover { transform: translateY(-3px) scale(1.04) !important; }
  .lk-kartya { transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease !important; }
  .lk-kartya:hover { transform: translateY(-8px) scale(1.03) !important; }
`;

// ============ FELADAT FEJLÉC ============
// Minden feladat tetején megjelenő sáv: szülői felügyelet jelzés +
// érthető, pontos feladatleírás.
function FeladatFejlec({ leiras, szin, children }) {
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
      <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', justifyContent: 'center'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '8px', background: `${szin}18`, border: `2px solid ${szin}50`, borderRadius: '16px', padding: '10px 20px', fontWeight: 800, fontSize: '17px', color: szin}}>
          👨‍👩‍👧 Szülő felügyelete mellett ajánlott
        </div>
      </div>
      {leiras && (
        <div style={{background: '#fff9e6', border: '2px solid #ffd93d', borderRadius: '18px', padding: '1rem 1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '19px', fontWeight: '700', color: '#7a5900', margin: 0, lineHeight: 1.6}}>📋 {leiras}</p>
        </div>
      )}
      {children}
    </div>
  );
}

// ============ LECKE ADATOK ============
const leckek = [
  {
    id: 1, cim: 'Mi az Internet?', ikon: '🌐', szin: '#4d96ff',
    video: '/lecke1_video.mp4',
    leiras: 'Fedezd fel az internet világát Cyber Robival!',
    feladatok: [
      {
        id: 'f1_1', tipus: 'valogato',
        cim: 'Húzd a helyére! 🌍',
        kerdes: 'Internet vagy való élet?',
        leiras: 'Húzd (vagy koppints, majd válaszd ki a dobozt) minden képet a megfelelő helyre: ami az interneten történik, az az Internet dobozba kerüljön, ami a valós életben, az a Való élet dobozba!',
        elemek: [
          { kep: { kep: '/jatekok/videohivas.png', emoji: '📹' }, felirat: 'Videóhívás', kategoria: 'internet' },
          { kep: { kep: '/jatekok/mesenezes.png', emoji: '🎬' }, felirat: 'Mese', kategoria: 'internet' },
          { kep: { kep: '/jatekok/fotonezes.png', emoji: '📷' }, felirat: 'Fotónézés', kategoria: 'internet' },
          { kep: { kep: '/jatekok/uzenetkuldes.png', emoji: '💌' }, felirat: 'Üzenetküldés', kategoria: 'internet' },
          { kep: { kep: '/jatekok/zenehallgatas.png', emoji: '🎵' }, felirat: 'Zenehallgatás', kategoria: 'internet' },
          { kep: { kep: '/jatekok/foci.png', emoji: '⚽' }, felirat: 'Foci', kategoria: 'valo_elet' },
          { kep: { kep: '/jatekok/bicikli.png', emoji: '🚲' }, felirat: 'Bicikli', kategoria: 'valo_elet' },
          { kep: { kep: '/jatekok/kirakozas.png', emoji: '🧩' }, felirat: 'Kirakózás', kategoria: 'valo_elet' },
          { kep: { kep: '/jatekok/kutyasetaltatas.png', emoji: '🐕' }, felirat: 'Kutyasétáltatás', kategoria: 'valo_elet' },
        ],
        kategoriak: [
          { id: 'internet', felirat: '🌍 Internet', szin: '#4d96ff' },
          { id: 'valo_elet', felirat: '🏞️ Való élet', szin: '#6bcb77', szimbolum: '🏞️' },
        ],
        visszajelzes_jo: 'Ügyes vagy! 🌐',
        visszajelzes_rossz: 'Nézd meg újra!',
      },
      {
        id: 'f1_2', tipus: 'mi_nem_illik',
        cim: 'Mi nem illik oda? 🤔',
        kerdes: 'Koppints arra, ami nem internetes dolog!',
        leiras: 'Nézd meg a négy képet, és koppints arra az egyre, ami szerinted NEM az internethez tartozik!',
        korok: [
          { elemek: ['📱', '💻', '🌍', '🌳'], masikIndex: 3 },
          { elemek: ['🎬', '📧', '🚲', '💬'], masikIndex: 2 },
          { elemek: ['⚽', '🎮', '📷', '🎵'], masikIndex: 0 },
          { elemek: ['📺', '🐕', '💻', '📱'], masikIndex: 1 },
          { elemek: ['🎨', '🌍', '📧', '🎬'], masikIndex: 0 },
        ],
      },
      {
        id: 'f1_3', tipus: 'parosito',
        cim: 'Párosítsd az egyformákat! 🔗',
        kerdes: 'Kösd össze az egyforma ikonokat!',
        leiras: 'Koppints egy ikonra a bal oldalon, majd koppints a jobb oldalon arra, amelyik pontosan ugyanaz — így párosítod össze őket!',
        elemek: ['📱', '💻', '🌍', '📧', '🎮', '📷'],
      },
    ],
  },
  {
    id: 2, cim: 'Titkaim védelme', ikon: '🤫', szin: '#c77dff',
    video: '/lecke2_video.mp4',
    leiras: 'Van, amit csak a szülőknek mondunk el!',
    feladatok: [
      {
        id: 'f2_1', tipus: 'valogato',
        cim: 'Elmondhatom vagy titok? 🤫',
        kerdes: 'Húzd a jó dobozba!',
        leiras: 'Nézd meg minden képet, és döntsd el: ezt nyugodtan elmondhatod bárkinek, vagy inkább titok, amit csak a szüleiddel osztasz meg?',
        elemek: [
          { kep: { kep: '/jatekok/haz.png', emoji: '🏠' }, felirat: 'Lakcím', kategoria: 'titok' },
          { kep: { kep: '/jatekok/telefon.png', emoji: '☎️' }, felirat: 'Telefonszám', kategoria: 'titok' },
          { kep: { kep: '/jatekok/jelszo_lakat.png', emoji: '🔑' }, felirat: 'Jelszó', kategoria: 'titok' },
          { kep: { kep: '/jatekok/iskola.png', emoji: '🏫' }, felirat: 'Iskolám neve', kategoria: 'titok' },
          { kep: { kep: '/jatekok/torta.png', emoji: '🎂' }, felirat: 'Kedvenc torta', kategoria: 'szabad' },
          { kep: { kep: '/jatekok/kedvenc_allat.png', emoji: '🐶' }, felirat: 'Kedvenc állat', kategoria: 'szabad' },
          { kep: { kep: '/jatekok/kedvenc_szin.png', emoji: '🎨' }, felirat: 'Kedvenc szín', kategoria: 'szabad' },
          { kep: { kep: '/jatekok/kedvenc_sport.png', emoji: '⚽' }, felirat: 'Kedvenc sport', kategoria: 'szabad' },
        ],
        kategoriak: [
          { id: 'szabad', felirat: '🟢 Elmondhatom', szin: '#34a853' },
          { id: 'titok', felirat: '🔒 Titok', szin: '#ea4335' },
        ],
        visszajelzes_jo: 'Szuper! Tudod mi a titok! 🔒',
        visszajelzes_rossz: 'A lakcím és a jelszó mindig titok!',
      },
      {
        id: 'f2_2', tipus: 'osszekoto',
        cim: 'Rajzold körbe a lakatot! 🔒',
        leiras: 'Nyomd le az ujjad/egeret az elején, és húzd végig a pontokon, hogy megrajzold a lakatot, ami a titkaidat védi!',
        celEmoji: '🔒',
        celNev: 'a lakatot',
        pontok: [
          {x: 150, y: 200}, {x: 150, y: 150}, {x: 170, y: 110}, {x: 200, y: 95}, {x: 230, y: 110},
          {x: 250, y: 150}, {x: 250, y: 200}, {x: 280, y: 200}, {x: 280, y: 320}, {x: 120, y: 320}, {x: 120, y: 200},
        ],
      },
      {
        id: 'f2_3', tipus: 'valaszd_ki',
        cim: 'Árnyékkereső 🔍',
        leiras: 'A halványan látszó ikon egy "árnyék" — koppints az alatta lévő lehetőségek közül arra a színes ikonra, amelyik pontosan ugyanaz!',
        korok: [
          { kerdes: 'Melyik ikon illik az árnyékhoz?', promptEmoji: '🔒', promptArnyek: true, opciok: [
            { emoji: '📱', felirat: 'Telefon', helyes: false }, { emoji: '🔒', felirat: 'Lakat', helyes: true },
            { emoji: '🏠', felirat: 'Ház', helyes: false }, { emoji: '👨‍👩‍👧', felirat: 'Család', helyes: false },
          ]},
          { kerdes: 'Melyik ikon illik az árnyékhoz?', promptEmoji: '🏠', promptArnyek: true, opciok: [
            { emoji: '🏠', felirat: 'Ház', helyes: true }, { emoji: '🔑', felirat: 'Kulcs', helyes: false },
            { emoji: '📞', felirat: 'Telefon', helyes: false }, { emoji: '🎂', felirat: 'Torta', helyes: false },
          ]},
          { kerdes: 'Melyik ikon illik az árnyékhoz?', promptEmoji: '🔑', promptArnyek: true, opciok: [
            { emoji: '🐶', felirat: 'Kutya', helyes: false }, { emoji: '🎨', felirat: 'Festék', helyes: false },
            { emoji: '🔑', felirat: 'Kulcs', helyes: true }, { emoji: '⚽', felirat: 'Labda', helyes: false },
          ]},
          { kerdes: 'Melyik ikon illik az árnyékhoz?', promptEmoji: '📱', promptArnyek: true, opciok: [
            { emoji: '📱', felirat: 'Telefon', helyes: true }, { emoji: '🏫', felirat: 'Iskola', helyes: false },
            { emoji: '🎂', felirat: 'Torta', helyes: false }, { emoji: '🐶', felirat: 'Kutya', helyes: false },
          ]},
          { kerdes: 'Melyik ikon illik az árnyékhoz?', promptEmoji: '👨‍👩‍👧', promptArnyek: true, opciok: [
            { emoji: '🏠', felirat: 'Ház', helyes: false }, { emoji: '👤', felirat: 'Idegen', helyes: false },
            { emoji: '🔒', felirat: 'Lakat', helyes: false }, { emoji: '👨‍👩‍👧', felirat: 'Család', helyes: true },
          ]},
        ],
      },
      {
        id: 'f2_4', tipus: 'buborek',
        cim: 'Buborékvadászat 🫧',
        kerdes: 'Csak ezeket pukkantsd ki!',
        leiras: 'Figyeld meg, mely buborékok jelennek meg felfelé úszva, és csak azokat pukkantsd ki, amik a biztonságot jelentik — a többit hagyd elúszni!',
        gyujtheto: ['🔒', '🛡️', '👨‍👩‍👧'],
        veszelyek: ['👤', '⚠️'],
      },
    ],
  },
  {
    id: 3, cim: 'Furcsa üzenet érkezett!', ikon: '📱', szin: '#ff6b9d',
    konyv: {
      cim: 'Robi és a furcsa üzenet',
      oldalak: [
        { szoveg: 'Robi egy szép nyári délután a tableten játszott a szobájában. A nap besütött az ablakon, és Robi nagyon boldog volt. A kedvenc játékában éppen egy izgalmas szinten járt!', ikon: '🎮', illusztracio: '📱☀️', haatter: 'linear-gradient(135deg, #fce4ec, #f3e5f5)' },
        { szoveg: 'Egyszer csak csörrent a tablet. Egy üzenet érkezett - de nem a barátjától! Egy ismeretlen feladótól jött az üzenet. Robi megállt és elolvasta: "Szia! Mondd meg hol laksz és mi a neved!"', ikon: '😟', illusztracio: '😟💬', haatter: 'linear-gradient(135deg, #fff8e1, #fce4ec)' },
        { szoveg: 'Robi szíve gyorsabban kezdett verni. Ez furcsa volt. A barátai soha nem kérdeznek ilyet - ők már tudják hol lakik Robi! "Ez valaki idegen" - gondolta Robi. "Mit csináljak?"', ikon: '🤔', illusztracio: '🤔💭', haatter: 'linear-gradient(135deg, #fff3e0, #fce4ec)' },
        { szoveg: 'Robinak eszébe jutott, amit anyukája mondott: "Ha valami furcsa történik az interneten, azonnal gyere hozzám!" Robi letette a tabletet és kiszaladt a konyhába.', ikon: '🏃', illusztracio: '🏃👩', haatter: 'linear-gradient(135deg, #e8f5e9, #e3f2fd)' },
        { szoveg: '"Anya! Anya! Valami furcsa üzenet jött!" - kiáltotta Robi. Anyuka azonnal abbahagyta a főzést és odament. Megnézte az üzenetet és komolyan bólintott.', ikon: '👩', illusztracio: '👩‍👦💕', haatter: 'linear-gradient(135deg, #e3f2fd, #f3e5f5)' },
        { szoveg: '"Nagyon ügyes voltál hogy azonnal szóltál!" - mondta anyuka és megölelte Robit. "Ez az üzenet valóban gyanús. Soha ne válaszolj ilyen üzenetre. Most együtt töröljük!"', ikon: '🤗', illusztracio: '🤗✅', haatter: 'linear-gradient(135deg, #e8f5e9, #f9fbe7)' },
        { szoveg: 'Anyuka törölte az üzenetet és megmagyarázta: "Az interneten vannak emberek akik furcsán viselkednek. Ha valami nem tetszik, MINDIG gyere hozzám! Én mindig segítek!" Robi boldogan bólintott.', ikon: '🌟', illusztracio: '🌟💪', haatter: 'linear-gradient(135deg, #fff8e1, #fffde7)', szabaly: 'Ha furcsa üzenet jön → NE válaszolj → Szólj AZONNAL a szülőknek! 🏃' },
      ],
    },
    feladatok: [
      {
        id: 'f3_1', tipus: 'valogato',
        cim: 'Ismerős vagy idegen? 🚦',
        kerdes: 'Húzd a jó helyre!',
        leiras: 'Nézd meg mindegyik képet, és húzd az "Ismerős" dobozba, ha valakit ismersz, vagy az "Idegen" dobozba, ha nem ismered!',
        elemek: [
          { kep: { kep: '/jatekok/anya.png', emoji: '👩' }, felirat: 'Anya', kategoria: 'ismeros' },
          { kep: { kep: '/jatekok/apa.png', emoji: '👨' }, felirat: 'Apa', kategoria: 'ismeros' },
          { kep: { kep: '/jatekok/barat.png', emoji: '👦' }, felirat: 'A barátod', kategoria: 'ismeros' },
          { kep: { kep: '/jatekok/ismeretlen.png', emoji: '👤' }, felirat: 'Idegen profil', kategoria: 'idegen' },
          { kep: { kep: '/jatekok/ismeretlen.png', emoji: '👤' }, felirat: 'Valaki, akit sosem láttál', kategoria: 'idegen' },
        ],
        kategoriak: [
          { id: 'ismeros', felirat: '🟢 Ismerős', szin: '#34a853' },
          { id: 'idegen', felirat: '🔴 Idegen', szin: '#ea4335' },
        ],
        visszajelzes_jo: 'Szuper! Felismered az idegeneket! 🚦',
        visszajelzes_rossz: 'Figyelj, kit ismersz és kit nem!',
      },
      {
        id: 'f3_2', tipus: 'keresd_meg', szuloSegitseg: true,
        cim: 'Keresd meg! 🚩',
        leiras: 'A telefon képernyőjén sok ikon van összekeveredve. Először keresd meg a zászlókat, utána a felkiáltójeleket, ami a gyanús üzeneteket jelöli!',
        korok: [
          { kerdes: 'Először találd meg a zászlókat! 🚩', racs: [
            { emoji: '💬', cel: false }, { emoji: '📷', cel: false }, { emoji: '🎵', cel: false }, { emoji: { kep: '/jatekok/piros_zaszlo.png', emoji: '🚩' }, cel: true },
            { emoji: '📺', cel: false }, { emoji: '🔔', cel: false }, { emoji: '📞', cel: false }, { emoji: '🎮', cel: false },
            { emoji: { kep: '/jatekok/piros_zaszlo.png', emoji: '🚩' }, cel: true }, { emoji: '📸', cel: false }, { emoji: { kep: '/jatekok/piros_zaszlo.png', emoji: '🚩' }, cel: true }, { emoji: '🎬', cel: false },
          ]},
          { kerdes: 'Most találd meg a felkiáltójeleket! ⚠️', racs: [
            { emoji: '🎧', cel: false }, { emoji: '📖', cel: false }, { emoji: '⚠️', cel: true }, { emoji: '🖼️', cel: false },
            { emoji: '🎹', cel: false }, { emoji: '⚠️', cel: true }, { emoji: '🧸', cel: false }, { emoji: '⚙️', cel: false },
            { emoji: '🚲', cel: false }, { emoji: '⚠️', cel: true }, { emoji: '🐶', cel: false }, { emoji: '⚽', cel: false },
          ]},
        ],
      },
      {
        id: 'f3_3', tipus: 'kapdel',
        cim: 'Útvonal Robinak! 🏃',
        kerdes: 'Vezesd el Robit a családhoz!',
        leiras: 'Mozgasd a kosarat a nyilakkal balra-jobbra, hogy Robi elkapja a jó dolgokat, és kikerülje a veszélyeket az útja során!',
        gyujtheto: [{ kep: '/jatekok/csalad.png', emoji: '👨‍👩‍👧' }, '❤️', '⭐'],
        veszelyek: ['👤', '⚠️'],
      },
      {
        id: 'f3_4', tipus: 'osszekoto',
        cim: 'Rajzold körbe a pajzsot! 🛡️',
        leiras: 'Nyomd le az ujjad/egeret az elején, és húzd végig a pontokon, hogy megrajzold a védelmező pajzsot!',
        celEmoji: '🛡️',
        celNev: 'a pajzsot',
        pontok: [
          {x: 200, y: 40}, {x: 320, y: 90}, {x: 320, y: 200}, {x: 260, y: 320}, {x: 200, y: 360},
          {x: 140, y: 320}, {x: 80, y: 200}, {x: 80, y: 90},
        ],
      },
    ],
  },
  {
    id: 4, cim: 'Biztonságos játék', ikon: '🎮', szin: '#6bcb77',
    konyv: {
      cim: 'Tomika és a furcsa játékos',
      oldalak: [
        { szoveg: 'Tomika imádott online játszani. Minden nap délután, ha elvégezte a házi feladatát, apa bekapcsolta a számítógépet és Tomika játszhatott egy kicsit.', ikon: '🎮', illusztracio: '🎮😊', haatter: 'linear-gradient(135deg, #e8f5e9, #f9fbe7)' },
        { szoveg: 'Az egyik kedvenc játékában egy nap egy új játékos csatlakozott. "Szia! Én vagyok SuperGamer99! Játsszunk együtt!" írta. Tomikának ez tetszett - jó volt csapatban játszani!', ikon: '👾', illusztracio: '👾🎮', haatter: 'linear-gradient(135deg, #e3f2fd, #e8f5e9)' },
        { szoveg: 'Néhány perc múlva azonban a SuperGamer99 furcsát kérdezett: "Hé, te milyen iskolába jársz? Hol laksz? Hány éves vagy?" Tomika megállt. Ez nem volt normális játékos kérdés!', ikon: '🤔', illusztracio: '🤔❓', haatter: 'linear-gradient(135deg, #fff8e1, #fffde7)' },
        { szoveg: '"A játékban soha nem kérdezik meg hol lakik valaki" - gondolta Tomika. "Ez valaki idegen az interneten, nem egy igazi barát!" Tomika azonnal leállította a játékot.', ikon: '🛑', illusztracio: '🛑💡', haatter: 'linear-gradient(135deg, #fce4ec, #fff3e0)' },
        { szoveg: 'Tomika kiszaladt a nappaliba ahol apa olvasott. "Apa! A játékban valaki furcsákat kérdezett!" Apa azonnal lerakta a könyvet és felállt. "Mesélj mindent!"', ikon: '👨', illusztracio: '👨💪', haatter: 'linear-gradient(135deg, #e3f2fd, #e8f5e9)' },
        { szoveg: '"Nagyon okosan cselekedtél!" - mondta apa. "Az online játékban az emberek idegenek lehetnek, még ha barátságosak is. Soha ne mondj meg semmit magadról idegeneknek!"', ikon: '💡', illusztracio: '👨💡', haatter: 'linear-gradient(135deg, #f3e5f5, #fce4ec)' },
        { szoveg: 'Apa segített Tomikának bejelenteni a gyanús játékost és aztán együtt folytatták a játékot. Tomika megtanulta: ha valami furcsa a játékban → szólj azonnal a szülőknek! 🎮🛡️', ikon: '🛡️', illusztracio: '🎮🛡️', haatter: 'linear-gradient(135deg, #e8f5e9, #f9fbe7)', szabaly: 'A játékban senki ne tudja: hol laksz, mi a neved, melyik iskolába jársz! → Ha kérdezik: szólj a szülőknek! 🎮' },
      ],
    },
    feladatok: [
      {
        id: 'f4_1', tipus: 'keresd_meg',
        cim: 'Kincskereső 🔍',
        leiras: 'Először keresd meg a lakatokat, utána a zászlókat, végül a pajzsokat — mindegyikből kettő van elrejtve!',
        korok: [
          { kerdes: 'Először találd meg a lakatokat! 🔒', racs: [
            { emoji: { kep: '/jatekok/lakat.png', emoji: '🔒' }, cel: true }, { emoji: '🎈', cel: false }, { emoji: '⭐', cel: false }, { emoji: '🎵', cel: false },
            { emoji: '📷', cel: false }, { emoji: { kep: '/jatekok/jatek_kontroller.png', emoji: '🎮' }, cel: false }, { emoji: '🧸', cel: false }, { emoji: '🎨', cel: false },
            { emoji: { kep: '/jatekok/lakat.png', emoji: '🔒' }, cel: true }, { emoji: '🐶', cel: false }, { emoji: '🎧', cel: false }, { emoji: '📖', cel: false },
          ]},
          { kerdes: 'Most találd meg a zászlókat! 🚩', racs: [
            { emoji: '🚲', cel: false }, { emoji: '🚩', cel: true }, { emoji: '⚽', cel: false }, { emoji: '🖼️', cel: false },
            { emoji: '⚙️', cel: false }, { emoji: '🎈', cel: false }, { emoji: '🚩', cel: true }, { emoji: '⭐', cel: false },
            { emoji: '🎵', cel: false }, { emoji: '📷', cel: false }, { emoji: '🧸', cel: false }, { emoji: '🎨', cel: false },
          ]},
          { kerdes: 'Végül találd meg a pajzsokat! 🛡️', racs: [
            { emoji: { kep: '/jatekok/pajzs.png', emoji: '🛡️' }, cel: true }, { emoji: '🐶', cel: false }, { emoji: '🎧', cel: false }, { emoji: '📖', cel: false },
            { emoji: '🖼️', cel: false }, { emoji: '⚙️', cel: false }, { emoji: { kep: '/jatekok/pajzs.png', emoji: '🛡️' }, cel: true }, { emoji: '🚲', cel: false },
            { emoji: '⚽', cel: false }, { emoji: '🎈', cel: false }, { emoji: '⭐', cel: false }, { emoji: '🎵', cel: false },
          ]},
        ],
      },
      {
        id: 'f4_2', tipus: 'memoria',
        cim: 'Memória 🧠',
        kerdes: 'Keresd az egyforma képeket!',
        leiras: 'Koppints két lapra egymás után — ha egyforma kép van rajtuk, nyitva maradnak, ha nem, visszafordulnak. Találd meg az összes párt!',
        kepek: [{ kep: '/jatekok/jatek_kontroller.png', emoji: '🎮' }, { kep: '/jatekok/lakat.png', emoji: '🔒' }, { kep: '/jatekok/pajzs.png', emoji: '🛡️' }, { kep: '/jatekok/apa.png', emoji: '👨' }, '⭐', '🏠'],
        visszajelzes_jo: 'Játékbiztonsági mester vagy! 🎮🏆',
        visszajelzes_rossz: 'Próbáld újra!',
      },
      {
        id: 'f4_3', tipus: 'valaszd_ki',
        cim: 'Sorozat folytatása 🧩',
        leiras: 'Figyeld meg a mintát, és koppints arra a képre, ami logikusan folytatja a sorozatot!',
        korok: [
          { kerdes: '🛡️ 🛡️ 🛡️ ❓ — mi jön ezután?', opciok: [
            { emoji: '🛡️', felirat: 'Pajzs', helyes: true }, { emoji: '📱', felirat: 'Telefon', helyes: false }, { emoji: '🍎', felirat: 'Alma', helyes: false },
          ]},
          { kerdes: '⭐ 🛡️ ⭐ 🛡️ ❓ — mi jön ezután?', opciok: [
            { emoji: '⭐', felirat: 'Csillag', helyes: true }, { emoji: '🎈', felirat: 'Lufi', helyes: false }, { emoji: '🚲', felirat: 'Bicikli', helyes: false },
          ]},
          { kerdes: '🔒 🔒 🎈 🔒 🔒 ❓ — mi jön ezután?', opciok: [
            { emoji: '🎈', felirat: 'Lufi', helyes: true }, { emoji: '⚽', felirat: 'Labda', helyes: false }, { emoji: '🍎', felirat: 'Alma', helyes: false },
          ]},
          { kerdes: '🎮 🎨 🎮 🎨 🎮 ❓ — mi jön ezután?', opciok: [
            { emoji: '🎨', felirat: 'Festék', helyes: true }, { emoji: '🎈', felirat: 'Lufi', helyes: false }, { emoji: '🔑', felirat: 'Kulcs', helyes: false },
          ]},
          { kerdes: '⚽ ⚽ 🚲 ⚽ ⚽ ❓ — mi jön ezután?', opciok: [
            { emoji: '🚲', felirat: 'Bicikli', helyes: true }, { emoji: '🧸', felirat: 'Maci', helyes: false }, { emoji: '📷', felirat: 'Fényképezőgép', helyes: false },
          ]},
        ],
      },
      {
        id: 'f4_4', tipus: 'kirakos',
        cim: 'Puzzle 🧩',
        leiras: 'Koppints a csempékre sorban 1-től 8-ig, hogy felfedd a képet alattuk!',
        darabszam: 8,
        kep: '/jatekok/robi_jatekban.png',
        kepEmoji: '🤖',
      },
    ],
  },
  {
    id: 5, cim: 'Képernyőidő', ikon: '⏰', szin: '#ff6b35',
    konyv: {
      cim: 'Zsófi és az Idővarázsló',
      oldalak: [
        { szoveg: 'Zsófi nagyon szeretett tableten játszani és videókat nézni. Minden nap amint hazaért az iskolából, rögtön a tabletért nyúlt. Játszott, nézett, játszott, nézett...', ikon: '📱', illusztracio: '📱😊', haatter: 'linear-gradient(135deg, #e3f2fd, #f0f7ff)' },
        { szoveg: 'Egyik este anyuka észrevette hogy Zsófi már 4 órája a tabletet nézi. "Zsófi, hogy érzed magad?" - kérdezte anyuka. Zsófi dörzsölte a szemét. "Egy kicsit fáj a fejem..."', ikon: '😔', illusztracio: '📱😔', haatter: 'linear-gradient(135deg, #fce4ec, #fff3e0)' },
        { szoveg: '"Gyere, üljünk le és beszéljünk!" - mondta anyuka. Leültek a kanapéra. Anyuka elmagyarázta: "A szemed és az agyad is elfárad ha sokáig nézi a képernyőt. Pihenni kell!"', ikon: '👩', illusztracio: '👩💕', haatter: 'linear-gradient(135deg, #e8f5e9, #f9fbe7)' },
        { szoveg: '"Napi 1-2 óra képernyőzés elég a te korodban" - mondta anyuka. "Utána ki kell menni játszani, mozogni, olvasni - vagy csak pihenni!" Zsófi kicsit szomorú lett.', ikon: '⏰', illusztracio: '⏰📋', haatter: 'linear-gradient(135deg, #fff8e1, #fffde7)' },
        { szoveg: '"De anya, mi van ha a játék éppen a legjobb részénél tart?" - kérdezte Zsófi. "Akkor is le kell tenni!" - mondta anyuka mosolyogva. "A szemed és az egészséged fontosabb!"', ikon: '👀', illusztracio: '👀💡', haatter: 'linear-gradient(135deg, #f3e5f5, #fce4ec)' },
        { szoveg: 'Másnap Zsófi kiment a kertbe focizni a szomszéd gyerekekkel. Annyira jó volt! Nevetett, futott, esett és felállt. Este sokkal jobban aludt mint amikor sokat tabletezett.', ikon: '⚽', illusztracio: '⚽😊', haatter: 'linear-gradient(135deg, #e8f5e9, #f9fbe7)' },
        { szoveg: '"Anyukának igaza volt!" - gondolta Zsófi. Ettől fogva együtt döntötték el mikor jön a képernyős idő. Ha Zsófi el is felejtette volna, anyuka mindig emlékeztette! 🌟', ikon: '🌟', illusztracio: '👩‍👧🌟', haatter: 'linear-gradient(135deg, #fff8e1, #fffde7)', szabaly: 'Napi 1-2 óra képernyő elég! Ha túl sok → szólj a szülőknek! ⏰' },
      ],
    },
    feladatok: [
      {
        id: 'f5_1', tipus: 'sorba_rendezes',
        cim: 'Napirend puzzle ☀️',
        kerdes: 'Rakd sorba a napodat!',
        leiras: 'Koppints az ikonokra abban a sorrendben, ahogy egy nap valóban lezajlik: reggeltől estig!',
        sorrend: [{ kep: '/jatekok/nap.png', emoji: '🌞' }, { kep: '/jatekok/etkezes.png', emoji: '🍽️' }, { kep: '/jatekok/iskolataska.png', emoji: '🏫' }, { kep: '/jatekok/foci.png', emoji: '⚽' }, '💻', { kep: '/jatekok/furdes.png', emoji: '🛁' }, { kep: '/jatekok/alvas.png', emoji: '😴' }],
      },
      {
        id: 'f5_2', tipus: 'buborek',
        cim: 'Lufipukkasztó 🎈',
        kerdes: 'Csak ezeket pukkantsd ki!',
        leiras: 'A felszálló lufik közül csak azokat pukkantsd ki, amik egészséges, képernyő nélküli tevékenységet mutatnak!',
        gyujtheto: ['⚽', '🚲', '📖', '🐶'],
        veszelyek: ['📱', '📺'],
      },
      {
        id: 'f5_3', tipus: 'keresd_meg',
        cim: 'Keresd az egészséges dolgokat! 🔍',
        leiras: 'Először keresd meg a labdákat (mozgás), utána a könyveket (olvasás) — ezek segítenek kiegyensúlyozni a képernyőidőt!',
        korok: [
          { kerdes: 'Először találd meg a labdákat! ⚽', racs: [
            { emoji: '📱', cel: false }, { emoji: '⚽', cel: true }, { emoji: '📺', cel: false }, { emoji: '🎮', cel: false },
            { emoji: '⚽', cel: true }, { emoji: '💻', cel: false }, { emoji: '🚲', cel: false }, { emoji: '🐶', cel: false },
            { emoji: '📷', cel: false }, { emoji: '⚽', cel: true }, { emoji: '🎧', cel: false }, { emoji: '📱', cel: false },
          ]},
          { kerdes: 'Most találd meg a könyveket! 📖', racs: [
            { emoji: '📺', cel: false }, { emoji: '📖', cel: true }, { emoji: '🎮', cel: false }, { emoji: '💻', cel: false },
            { emoji: '🧸', cel: false }, { emoji: '📖', cel: true }, { emoji: '📱', cel: false }, { emoji: '🎵', cel: false },
            { emoji: '📖', cel: true }, { emoji: '🎈', cel: false }, { emoji: '📺', cel: false }, { emoji: '🎨', cel: false },
          ]},
        ],
      },
      {
        id: 'f5_4', tipus: 'szinezo',
        cim: 'Színező 🎨',
        leiras: 'Válassz egy színt a paletta alján, majd húzd az ujjad (vagy az egeret) a képen, hogy kiszínezd Cyber Robit!',
        kep: '/jatekok/robi_vonalrajz.png',
      },
    ],
  },
  {
    id: 6, cim: 'Ha megijedek', ikon: '🆘', szin: '#ffd93d',
    konyv: {
      cim: 'Marci és a Fényes Pajzs',
      oldalak: [
        { szoveg: 'Marci 7 éves volt és nagyon kíváncsi természetű. Szeretett minden mindent megnézni az interneten - videókat, képeket, rajzfilmeket. Anyuka mindig mellette volt amikor tabletezett.', ikon: '👦', illusztracio: '👦📱', haatter: 'linear-gradient(135deg, #e3f2fd, #f0f7ff)' },
        { szoveg: 'Egyik délután Marci egyedül nézett videókat. Egyik videóból a másikba kattintott, és egyszer csak egy nagyon ijesztő kép jelent meg a képernyőn! Marci szíve majdnem megállt!', ikon: '😨', illusztracio: '📱😨', haatter: 'linear-gradient(135deg, #fce8e6, #fff3e0)' },
        { szoveg: 'Marci remegő kézzel letette a tabletet. A szíve gyorsan vert és kicsit sírt is. De aztán eszébe jutott amit anyuka mondott: "Ha valami ijesztő, azonnal gyere hozzám!"', ikon: '😢', illusztracio: '😢💭', haatter: 'linear-gradient(135deg, #fce4ec, #f3e5f5)' },
        { szoveg: 'Marci felállt és odaszaladt anyukájához a nappaliba. "Anya! Anya! Valami nagyon ijesztőt láttam!" - mondta remegő hangon. Anyuka azonnal félretette az újságját.', ikon: '🏃', illusztracio: '🏃👩', haatter: 'linear-gradient(135deg, #e8f5e9, #e3f2fd)' },
        { szoveg: 'Anyuka lehajolt Marci szintjére és megölelte. "Jó hogy szóltál! Nagyon bátor voltál!" Marci beletemette az arcát anyuka vállába és lassan megnyugodott.', ikon: '🤗', illusztracio: '👩‍👦💕', haatter: 'linear-gradient(135deg, #e8f5e9, #f9fbe7)' },
        { szoveg: 'Anyuka megnézte a tabletet és beállított egy biztonsági szűrőt hogy ilyen videók ne jelenhessenek meg többé. "Látod? Együtt megoldottuk! Mindig szólj ha valami nem tetszik!"', ikon: '✅', illusztracio: '✅🛡️', haatter: 'linear-gradient(135deg, #fff8e1, #fffde7)' },
        { szoveg: 'Marcinak eszébe jutott az aranyszabály: Ha valami rossz, ijesztő vagy furcsa az interneten → ÁLLJ MEG → TEDD LE A TABLETET → SZÓLJ A SZÜLŐKNEK! Ők mindig segítenek! 🌟', ikon: '🌟', illusztracio: '🌟💪', haatter: 'linear-gradient(135deg, #e8f5e9, #f9fbe7)', szabaly: 'Ha megijedsz → Leteszed → Odamész a szülőkhöz → Elmondod! Ők mindig segítenek! 💕' },
      ],
    },
    feladatok: [
      {
        id: 'f6_1', tipus: 'osszekoto',
        cim: 'Rajzold körbe a szívet! ❤️',
        leiras: 'Nyomd le az ujjad/egeret az elején, és húzd végig a pontokon, hogy megrajzolj egy szívet — ami emlékeztet, hogy mindig van, aki szeret és segít!',
        celEmoji: '❤️',
        celNev: 'a szívet',
        pontok: [
          {x: 200, y: 150}, {x: 170, y: 100}, {x: 120, y: 100}, {x: 90, y: 140}, {x: 100, y: 190},
          {x: 200, y: 300}, {x: 300, y: 190}, {x: 310, y: 140}, {x: 280, y: 100}, {x: 230, y: 100},
        ],
      },
      {
        id: 'f6_2', tipus: 'racs',
        cim: 'Csillaggyűjtő ⭐',
        kerdes: 'Gyűjtsd össze a jó dolgokat!',
        leiras: 'A rácsban felbukkanó ikonok közül gyorsan koppints a csillagra, szívre és pajzsra — kerüld el a veszélyeket!',
        gyujtheto: [{ kep: '/jatekok/csillag.png', emoji: '⭐' }, { kep: '/jatekok/sziv.png', emoji: '❤️' }, { kep: '/jatekok/pajzs.png', emoji: '🛡️' }],
        veszelyek: ['👤', '⚠️'],
      },
      {
        id: 'f6_3', tipus: 'keresd_meg',
        cim: 'Keresd a biztonság jeleit! 🔍',
        leiras: 'Először keresd meg a pajzsokat, utána a szíveket — mindegyikből három van elrejtve!',
        korok: [
          { kerdes: 'Először találd meg a pajzsokat! 🛡️', racs: [
            { emoji: { kep: '/jatekok/pajzs.png', emoji: '🛡️' }, cel: true }, { emoji: '🎈', cel: false }, { emoji: '🎨', cel: false }, { emoji: '🧸', cel: false },
            { emoji: { kep: '/jatekok/pajzs.png', emoji: '🛡️' }, cel: true }, { emoji: '📷', cel: false }, { emoji: '🎮', cel: false }, { emoji: '🚲', cel: false },
            { emoji: '⚽', cel: false }, { emoji: { kep: '/jatekok/pajzs.png', emoji: '🛡️' }, cel: true }, { emoji: '📖', cel: false }, { emoji: '🎵', cel: false },
          ]},
          { kerdes: 'Most találd meg a szíveket! ❤️', racs: [
            { emoji: { kep: '/jatekok/sziv.png', emoji: '❤️' }, cel: true }, { emoji: '🖼️', cel: false }, { emoji: '⚙️', cel: false }, { emoji: '🐶', cel: false },
            { emoji: '🎧', cel: false }, { emoji: { kep: '/jatekok/sziv.png', emoji: '❤️' }, cel: true }, { emoji: '🎈', cel: false }, { emoji: '🎨', cel: false },
            { emoji: '🧸', cel: false }, { emoji: '📷', cel: false }, { emoji: { kep: '/jatekok/sziv.png', emoji: '❤️' }, cel: true }, { emoji: '🎮', cel: false },
          ]},
        ],
      },
      {
        id: 'f6_4', tipus: 'kirakos',
        cim: 'Nagy befejező kirakó 🧩',
        leiras: 'Koppints a csempékre sorban 1-től 12-ig, hogy felfedd a záró képet — Cyber Robit és a családot!',
        darabszam: 12,
        kep: '/jatekok/robi_csalad_zaro.png',
        kepEmoji: '🤖',
      },
    ],
  },
];

// ============ PLATFORMER JÁTÉK ============
function PlatformerJatek({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [kesz, setKesz] = useState(false);
  const [elet, setElet] = useState(3);
  const [pontok, setPontok] = useState(0);
  const [uzenet, setUzenet] = useState('');
  const [gameOver, setGameOver] = useState(false);
  const jatekRef = useRef({
    robiX: 80, robiY: 280, robiVY: 0, foldon: true,
    kameraX: 0, pontok: 0, elet: 3, fut: false,
    gyujtottek: [], veszelyElemek: [],
    bal: false, jobb: false, ugras: false, animId: null,
  });
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const J = jatekRef.current;
    const ctx = canvas.getContext('2d');
    J.fut = true;

    const W = 900, H = 480, TALAJ = 380, VILAG = 4500;
    const gyujthetok = feladat.gyujtheto || ['⭐'];
    const veszelyek_lista = feladat.veszelyek || ['⚠️'];

    J.gyujtottek = Array.from({length: 14}, (_, i) => ({
      x: 200 + i * 300 + (i % 3) * 50,
      y: TALAJ - 30 - (i % 4) * 70,
      emoji: gyujthetok[i % gyujthetok.length],
      megvan: false,
    }));

    J.veszelyElemek = Array.from({length: 10}, (_, i) => ({
      x: 320 + i * 380,
      y: TALAJ - 10,
      emoji: veszelyek_lista[i % veszelyek_lista.length],
    }));

    const keyDown = (e) => {
      if (e.key === 'ArrowLeft') J.bal = true;
      if (e.key === 'ArrowRight') J.jobb = true;
      if ((e.key === 'ArrowUp' || e.key === ' ') && J.foldon) { J.ugras = true; e.preventDefault(); }
    };
    const keyUp = (e) => {
      if (e.key === 'ArrowLeft') J.bal = false;
      if (e.key === 'ArrowRight') J.jobb = false;
    };
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);

    const platformok = [
      [350,280,140],[650,240,120],[950,260,140],[1250,230,120],
      [1550,250,140],[1850,220,120],[2150,260,140],[2450,240,120],
      [2750,250,140],[3050,230,120],[3350,260,140],[3650,240,120],
    ];

    const loop = () => {
      if (!J.fut) return;

      if (J.bal) J.robiX -= 5;
      if (J.jobb) J.robiX += 5;
      if (J.ugras) { J.robiVY = -14; J.foldon = false; J.ugras = false; }
      J.robiVY += 0.7;
      J.robiY += J.robiVY;

      // Platform ütközés
      let a_talajon = false;
      if (J.robiY >= TALAJ) { J.robiY = TALAJ; J.robiVY = 0; a_talajon = true; }
      platformok.forEach(([px, py, pw]) => {
        const screenPx = px - J.kameraX;
        if (J.robiX - J.kameraX > screenPx - 20 && J.robiX - J.kameraX < screenPx + pw + 20 && J.robiY >= py - 5 && J.robiY <= py + 20 && J.robiVY > 0) {
          J.robiY = py; J.robiVY = 0; a_talajon = true;
        }
      });
      J.foldon = a_talajon;
      J.robiX = Math.max(20, J.robiX);
      J.kameraX = Math.max(0, Math.min(J.robiX - 250, VILAG - W));

      // Gyűjtés
      J.gyujtottek.forEach(e => {
        if (!e.megvan && Math.abs(J.robiX - e.x) < 35 && Math.abs(J.robiY - e.y) < 35) {
          e.megvan = true; J.pontok++; setPontok(J.pontok);
          if (J.pontok >= 5) { J.fut = false; setKesz(true); }
        }
      });

      // Veszély
      J.veszelyElemek.forEach(e => {
        if (Math.abs(J.robiX - e.x) < 28 && Math.abs(J.robiY - e.y) < 28) {
          J.elet--; setElet(J.elet);
          J.robiX = 80; J.robiY = 280; J.robiVY = 0; J.kameraX = 0; J.foldon = true;
          setUzenet('😱 Jaj! Próbáld újra!');
          setTimeout(() => setUzenet(''), 1500);
          if (J.elet <= 0) { J.fut = false; setGameOver(true); }
        }
      });

      // Rajzolás
      ctx.clearRect(0, 0, W, H);

      // Ég gradient
      const eg = ctx.createLinearGradient(0, 0, 0, H);
      eg.addColorStop(0, '#87CEEB'); eg.addColorStop(1, '#E0F7FA');
      ctx.fillStyle = eg; ctx.fillRect(0, 0, W, H);

      // Felhők (parallax)
      ctx.fillStyle = 'rgba(255,255,255,0.88)';
      [[80,50,55],[250,35,70],[450,55,50],[680,42,65],[880,52,58],[1100,38,62]].forEach(([x,y,r]) => {
        const fx = ((x - J.kameraX * 0.25) % (W + 300) + W + 300) % (W + 300) - 150;
        ctx.beginPath(); ctx.ellipse(fx, y, r, r*0.55, 0, 0, Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.ellipse(fx + r*0.6, y + 5, r*0.7, r*0.4, 0, 0, Math.PI*2); ctx.fill();
      });

      // Platformok
      platformok.forEach(([px, py, pw]) => {
        const spx = px - J.kameraX;
        if (spx > -pw && spx < W + pw) {
          ctx.fillStyle = '#66BB6A'; ctx.fillRect(spx, py, pw, 20);
          ctx.fillStyle = '#4CAF50'; ctx.fillRect(spx, py, pw, 5);
          for (let g = spx; g < spx + pw; g += 12) {
            ctx.fillStyle = '#388E3C';
            ctx.fillRect(g, py - 4, 3, 6);
          }
        }
      });

      // Talaj
      ctx.fillStyle = '#66BB6A'; ctx.fillRect(0, TALAJ + 20, W, 35);
      ctx.fillStyle = '#4CAF50'; ctx.fillRect(0, TALAJ + 18, W, 5);
      ctx.fillStyle = '#795548'; ctx.fillRect(0, TALAJ + 55, W, H);
      for (let g = 0; g < W; g += 14) {
        ctx.fillStyle = '#388E3C'; ctx.fillRect(g, TALAJ + 16, 3, 6);
      }

      // Gyűjthető elemek
      ctx.font = '28px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      J.gyujtottek.forEach(e => {
        if (!e.megvan) {
          const ex = e.x - J.kameraX;
          if (ex > -50 && ex < W + 50) {
            const t = Date.now() / 500;
            const lebeg = Math.sin(t + e.x) * 5;
            ctx.fillText(e.emoji, ex, e.y + lebeg);
          }
        }
      });

      // Veszélyek
      J.veszelyElemek.forEach(e => {
        const ex = e.x - J.kameraX;
        if (ex > -50 && ex < W + 50) {
          ctx.font = '26px serif';
          ctx.fillText(e.emoji, ex, e.y);
        }
      });

      // Haladás jelző
      const haladPct = Math.min(J.robiX / VILAG, 1);
      ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.fillRect(10, 10, 200, 12); ctx.beginPath(); ctx.arc(10, 16, 6, 0, Math.PI*2); ctx.fill(); ctx.beginPath(); ctx.arc(210, 16, 6, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = szin; ctx.fillRect(10, 10, 200 * haladPct, 12);
      ctx.fillStyle = 'white'; ctx.font = '10px sans-serif'; ctx.textAlign = 'center'; ctx.fillText('🏁', 216, 18);

      // Robi
      ctx.font = '38px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('🤖', J.robiX - J.kameraX, J.robiY - 5);

      J.animId = requestAnimationFrame(loop);
    };

    J.animId = requestAnimationFrame(loop);
    return () => { J.fut = false; cancelAnimationFrame(J.animId); window.removeEventListener('keydown', keyDown); window.removeEventListener('keyup', keyUp); };
  }, []);

  const ujraKezdes = () => {
    const J = jatekRef.current;
    J.robiX = 80; J.robiY = 280; J.robiVY = 0; J.foldon = true;
    J.kameraX = 0; J.pontok = 0; J.elet = 3; J.fut = true;
    J.gyujtottek.forEach(e => e.megvan = false);
    setElet(3); setPontok(0); setKesz(false); setGameOver(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const loop = () => { if (!J.fut) return; J.animId = requestAnimationFrame(loop); };
    J.animId = requestAnimationFrame(loop);
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.25rem 1.5rem', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)', margin: '0 0 4px'}}>{feladat.cim}</p>
        <p style={{fontSize: '16px', color: 'var(--text-secondary)', margin: '0 0 4px', fontWeight: '700'}}>{feladat.kerdes}</p>
        <p style={{fontSize: '13px', color: '#aaa', margin: 0}}>⬅️➡️ mozgás | ⬆️ ugrás | Gyűjts 5-öt!</p>
      </div>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-card)', borderRadius: '16px', padding: '0.75rem 1.25rem', border: '2px solid var(--border-color)'}}>
        <div style={{display: 'flex', gap: '4px'}}>
          {[0,1,2].map(i => <span key={i} style={{fontSize: '22px', opacity: i < elet ? 1 : 0.2}}>❤️</span>)}
        </div>
        <div style={{fontSize: '15px', fontWeight: '800', color: szin}}>⭐ {pontok} / 5</div>
      </div>
      <div style={{position: 'relative', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.15)'}}>
        <canvas ref={canvasRef} width={900} height={480} style={{display: 'block', width: '100%', height: 'auto'}}/>
        {uzenet && (
          <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'rgba(0,0,0,0.75)', color: 'white', padding: '0.875rem 1.75rem', borderRadius: '16px', fontSize: '18px', fontWeight: '800', whiteSpace: 'nowrap'}}>
            {uzenet}
          </div>
        )}
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 64px)', gap: '8px', justifyContent: 'center'}}>
        <div/>
        <button onTouchStart={(e) => { e.preventDefault(); jatekRef.current.ugras = true; }} onMouseDown={() => { jatekRef.current.ugras = true; }} className="lk-btn"
          style={{width:'64px',height:'64px',background:`linear-gradient(135deg,${szin},${szin}cc)`,border:'none',borderRadius:'16px',fontSize:'26px',cursor:'pointer',color:'white',boxShadow:`0 4px 12px ${szin}40`}}>⬆️</button>
        <div/>
        <button onTouchStart={(e) => { e.preventDefault(); jatekRef.current.bal = true; }} onTouchEnd={() => { jatekRef.current.bal = false; }} onMouseDown={() => { jatekRef.current.bal = true; }} onMouseUp={() => { jatekRef.current.bal = false; }} onMouseLeave={() => { jatekRef.current.bal = false; }} className="lk-btn"
          style={{width:'64px',height:'64px',background:`linear-gradient(135deg,${szin},${szin}cc)`,border:'none',borderRadius:'16px',fontSize:'26px',cursor:'pointer',color:'white',boxShadow:`0 4px 12px ${szin}40`}}>⬅️</button>
        <div/>
        <button onTouchStart={(e) => { e.preventDefault(); jatekRef.current.jobb = true; }} onTouchEnd={() => { jatekRef.current.jobb = false; }} onMouseDown={() => { jatekRef.current.jobb = true; }} onMouseUp={() => { jatekRef.current.jobb = false; }} onMouseLeave={() => { jatekRef.current.jobb = false; }} className="lk-btn"
          style={{width:'64px',height:'64px',background:`linear-gradient(135deg,${szin},${szin}cc)`,border:'none',borderRadius:'16px',fontSize:'26px',cursor:'pointer',color:'white',boxShadow:`0 4px 12px ${szin}40`}}>➡️</button>
      </div>
      {kesz && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '22px', fontWeight: '900', color: '#1e7e34', margin: '0 0 1rem'}}>🎉 Szuper! Meggyűjtötted!</p>
          <button onClick={onKesz} className="lk-btn" style={{padding: '14px 36px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '17px', fontWeight: '800', cursor: 'pointer'}}>Tovább! 🚀</button>
        </div>
      )}
      {gameOver && !kesz && (
        <div style={{background: '#fce8e6', border: '2px solid #ea4335', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: '#c62828', margin: '0 0 1rem'}}>😔 Próbáld újra!</p>
          <button onClick={ujraKezdes} className="lk-btn" style={{padding: '14px 36px', background: 'linear-gradient(135deg, #f9ab00, #ff6d00)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '17px', fontWeight: '800', cursor: 'pointer'}}>🔄 Újra!</button>
        </div>
      )}
    </div>
  );
}

// ============ ÜZENET FELISMERŐ ============
function UzenetFelismeroFeladat({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [valaszok, setValaszok] = useState({});
  const [ellenorzott, setEllenorzott] = useState(false);
  const [helyes, setHelyes] = useState(false);

  const kattint = (i, ertek) => { if (ellenorzott) return; setValaszok(prev => ({ ...prev, [i]: ertek })); };
  const ellenorzes = () => { setHelyes(feladat.uzenetek.every((u, i) => valaszok[i] === u.jo)); setEllenorzott(true); };
  const ujra = () => { setValaszok({}); setEllenorzott(false); setHelyes(false); };
  const mindenMegvalaszolva = feladat.uzenetek.every((_, i) => valaszok[i] !== undefined);

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '22px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{feladat.kerdes}</p>
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
        {feladat.uzenetek.map((u, i) => {
          const kiv = valaszok[i];
          const helyesE = ellenorzott && kiv === u.jo;
          const rosszE = ellenorzott && kiv !== undefined && kiv !== u.jo;
          return (
            <div key={i} style={{background: helyesE ? '#e8f5e9' : rosszE ? '#fce8e6' : 'white', borderRadius: '20px', padding: '1rem 1.5rem', border: `2px solid ${helyesE ? '#34a853' : rosszE ? '#ea4335' : '#f0f0f0'}`, boxShadow: '0 2px 10px rgba(0,0,0,0.05)'}}>
              {u.kep && <div style={{textAlign: 'center', marginBottom: '0.5rem'}}><KepVagyEmoji ertek={u.kep} meret={200} /></div>}
              <p style={{fontSize: '30px', fontWeight: '800', color: '#2d3748', margin: '0 0 0.75rem'}}>{u.szoveg}</p>
              <div style={{display: 'flex', gap: '10px'}}>
                <button onClick={() => kattint(i, true)} className="lk-btn"
                  style={{flex: 1, padding: '14px', background: kiv === true ? (ellenorzott ? (u.jo ? '#34a853' : '#ea4335') : '#34a853') : 'white', border: `3px solid ${kiv === true ? (ellenorzott ? (u.jo ? '#34a853' : '#ea4335') : '#34a853') : '#34a853'}`, borderRadius: '16px', fontSize: '34px', cursor: 'pointer'}}>
                  ✅
                </button>
                <button onClick={() => kattint(i, false)} className="lk-btn"
                  style={{flex: 1, padding: '14px', background: kiv === false ? (ellenorzott ? (!u.jo ? '#34a853' : '#ea4335') : '#ea4335') : 'white', border: `3px solid ${kiv === false ? (ellenorzott ? (!u.jo ? '#34a853' : '#ea4335') : '#ea4335') : '#ea4335'}`, borderRadius: '16px', fontSize: '34px', cursor: 'pointer'}}>
                  🚨
                </button>
              </div>
              {ellenorzott && <p style={{fontSize: '15px', fontWeight: '700', color: helyesE ? '#1e7e34' : '#c62828', margin: '8px 0 0', padding: '6px 10px', background: helyesE ? '#e8f5e9' : '#fce8e6', borderRadius: '10px'}}>{u.magyarazat}</p>}
            </div>
          );
        })}
      </div>
      {ellenorzott && (
        <div style={{background: helyes ? '#e8f5e9' : '#fce8e6', border: `2px solid ${helyes ? '#34a853' : '#ea4335'}`, borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '22px', fontWeight: '900', color: helyes ? '#1e7e34' : '#c62828', margin: 0}}>{helyes ? feladat.visszajelzes_jo : feladat.visszajelzes_rossz}</p>
        </div>
      )}
      <div style={{display: 'flex', justifyContent: 'center'}}>
        {!ellenorzott ? (
          <button onClick={ellenorzes} disabled={!mindenMegvalaszolva} className="lk-btn"
            style={{padding: '16px 40px', background: !mindenMegvalaszolva ? '#f0f0f0' : `linear-gradient(135deg, ${szin}, ${szin}cc)`, color: !mindenMegvalaszolva ? '#bbb' : 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: !mindenMegvalaszolva ? 'not-allowed' : 'pointer'}}>
            ✅ Ellenőrzés
          </button>
        ) : helyes ? (
          <button onClick={onKesz} className="lk-btn" style={{padding: '16px 40px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: 'pointer'}}>Tovább! 🚀</button>
        ) : (
          <button onClick={ujra} className="lk-btn" style={{padding: '16px 40px', background: 'linear-gradient(135deg, #f9ab00, #ff6d00)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: 'pointer'}}>🔄 Próbáld újra!</button>
        )}
      </div>
    </div>
  );
}

// ============ SZÓLJ A SZÜLŐKNEK ============
function SzolySzuloknek({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [aktIndex, setAktIndex] = useState(0);
  const [kivalasztott, setKivalasztott] = useState(null);
  const [ellenorzott, setEllenorzott] = useState(false);
  const [hibak, setHibak] = useState(0);
  const [animSzulo, setAnimSzulo] = useState(false);

  const akt = feladat.helyzetek[aktIndex];
  const helyesE = ellenorzott && kivalasztott === 'jo';

  const valaszt = (tipus) => { if (ellenorzott) return; setKivalasztott(tipus); };
  const ellenorzes = () => {
    if (!kivalasztott) return;
    if (kivalasztott !== 'jo') setHibak(h => h + 1);
    else setAnimSzulo(true);
    setEllenorzott(true);
  };
  const tovabb = () => {
    if (aktIndex + 1 < feladat.helyzetek.length) {
      setAktIndex(p => p + 1); setKivalasztott(null); setEllenorzott(false); setAnimSzulo(false);
    } else {
      if (hibak === 0) onKesz();
      else { setAktIndex(0); setKivalasztott(null); setEllenorzott(false); setHibak(0); setAnimSzulo(false); }
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
          <p style={{fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)', margin: 0}}>{feladat.cim}</p>
          <div style={{background: `${szin}20`, color: szin, borderRadius: '50px', padding: '4px 14px', fontSize: '14px', fontWeight: '800'}}>{aktIndex + 1}/{feladat.helyzetek.length}</div>
        </div>
        <p style={{fontSize: '17px', color: 'var(--text-secondary)', margin: '0 0 1rem', fontWeight: '700'}}>{feladat.kerdes}</p>
        <div style={{background: `${szin}12`, border: `2px solid ${szin}30`, borderRadius: '18px', padding: '1.25rem', textAlign: 'center'}}>
          <p style={{fontSize: '24px', fontWeight: '800', color: '#2d3748', margin: 0, lineHeight: 1.5}}>{akt?.helyzet}</p>
        </div>
      </div>

      {animSzulo && (
        <div style={{textAlign: 'center', background: 'linear-gradient(135deg, #e8f5e9, #f1f8e9)', borderRadius: '20px', padding: '1.5rem', animation: 'felugrik 0.4s ease-out', border: '2px solid #34a853'}}>
          <div style={{fontSize: '56px', marginBottom: '0.5rem', animation: 'ugorj 0.6s ease-in-out 3'}}>🏃💨</div>
          <div style={{fontSize: '17px', fontWeight: '800', color: '#1e7e34', marginBottom: '0.75rem'}}>Robi szalad a szülőkhöz!</div>
          <div style={{fontSize: '56px', marginBottom: '0.5rem'}}>👨‍👩‍👧</div>
          <div style={{fontSize: '17px', fontWeight: '800', color: '#1e7e34'}}>A szülők segítenek! ❤️</div>
        </div>
      )}

      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
        <button onClick={() => valaszt('jo')} className="lk-btn"
          style={{padding: '2rem 1rem', background: kivalasztott === 'jo' ? (ellenorzott ? '#e8f5e9' : `${szin}20`) : 'white', border: `4px solid ${kivalasztott === 'jo' ? (ellenorzott ? '#34a853' : szin) : '#f0f0f0'}`, borderRadius: '24px', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'}}>
          <span style={{fontSize: '56px'}}>🏃👨‍👩‍👧</span>
          <span style={{fontSize: '14px', fontWeight: '800', color: 'var(--text-secondary)'}}>Szólok a szülőmnek</span>
        </button>
        <button onClick={() => valaszt('rossz')} className="lk-btn"
          style={{padding: '2rem 1rem', background: kivalasztott === 'rossz' ? (ellenorzott ? '#fce8e6' : '#ff000015') : 'white', border: `4px solid ${kivalasztott === 'rossz' ? '#ea4335' : '#f0f0f0'}`, borderRadius: '24px', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'}}>
          <span style={{fontSize: '56px'}}>🤐</span>
          <span style={{fontSize: '14px', fontWeight: '800', color: 'var(--text-secondary)'}}>Titokban tartom</span>
        </button>
      </div>

      {ellenorzott && (
        <div style={{background: helyesE ? '#e8f5e9' : '#fce8e6', border: `2px solid ${helyesE ? '#34a853' : '#ea4335'}`, borderRadius: '20px', padding: '1.25rem', textAlign: 'center'}}>
          <p style={{fontSize: '18px', fontWeight: '800', color: helyesE ? '#1e7e34' : '#c62828', margin: '0 0 4px'}}>{helyesE ? '✅ Pontosan!' : '❌ Ez nem jó!'}</p>
          <p style={{fontSize: '16px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{akt?.magyarazat}</p>
        </div>
      )}

      <div style={{display: 'flex', justifyContent: 'center'}}>
        {!ellenorzott ? (
          <button onClick={ellenorzes} disabled={!kivalasztott} className="lk-btn"
            style={{padding: '16px 40px', background: !kivalasztott ? '#f0f0f0' : `linear-gradient(135deg, ${szin}, ${szin}cc)`, color: !kivalasztott ? '#bbb' : 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: !kivalasztott ? 'not-allowed' : 'pointer'}}>
            ✅ Ellenőrzés
          </button>
        ) : (
          <button onClick={tovabb} className="lk-btn" style={{padding: '16px 40px', background: `linear-gradient(135deg, ${szin}, ${szin}cc)`, color: 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: 'pointer'}}>
            {aktIndex + 1 < feladat.helyzetek.length ? 'Következő →' : hibak === 0 ? 'Tovább! 🚀' : '🔄 Újra!'}
          </button>
        )}
      </div>
    </div>
  );
}

// ============ MEMÓRIA JÁTÉK ============
function MemoriaJatek({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [megtalalt, setMegtalalt] = useState([]);
  const [felfedett, setFelfedett] = useState([]);
  const [elsoKattintas, setElsoKattintas] = useState(null);
  const [blokkolt, setBlokkolt] = useState(false);

  // Valódi memóriajáték: minden kép pontosan kétszer szerepel,
  // az azonos képeket kell megtalálni (nem rokon fogalmakat).
  const kartyak = useState(() => {
    const osszes = feladat.kepek.flatMap((emoji, i) => [
      { id: `a${i}`, emoji, par: i },
      { id: `b${i}`, emoji, par: i },
    ]);
    return osszes.sort(() => Math.random() - 0.5);
  })[0];

  const kattint = (kartya) => {
    if (blokkolt || felfedett.includes(kartya.id) || megtalalt.some(m => m.includes(kartya.id))) return;
    const ujFelfedett = [...felfedett, kartya.id];
    setFelfedett(ujFelfedett);
    if (!elsoKattintas) {
      setElsoKattintas(kartya);
    } else {
      if (elsoKattintas.par === kartya.par && elsoKattintas.id !== kartya.id) {
        const ujMegtalalt = [...megtalalt, [elsoKattintas.id, kartya.id]];
        setMegtalalt(ujMegtalalt);
        setElsoKattintas(null); setFelfedett([]);
        if (ujMegtalalt.length === feladat.kepek.length) setTimeout(onKesz, 1000);
      } else {
        setBlokkolt(true);
        setTimeout(() => { setFelfedett([]); setElsoKattintas(null); setBlokkolt(false); }, 1000);
      }
    }
  };

  const megtalaltIds = megtalalt.flat();

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '17px', color: 'var(--text-secondary)', margin: '0 0 0.5rem', fontWeight: '700'}}>Keresd meg az egyforma képeket!</p>
        <span style={{fontSize: '17px', fontWeight: '800', color: szin}}>{megtalalt.length}/{feladat.kepek.length} pár megtalálva</span>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.9rem'}}>
        {kartyak.map((k) => {
          const latszik = felfedett.includes(k.id) || megtalaltIds.includes(k.id);
          const megvan = megtalaltIds.includes(k.id);
          return (
            <div key={k.id} onClick={() => kattint(k)} className="lk-btn"
              style={{height: '260px', borderRadius: '30px', cursor: latszik ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '56px', textAlign: 'center', padding: '6px', transition: 'all 0.3s', background: megvan ? `${szin}20` : latszik ? 'white' : `linear-gradient(135deg, ${szin}, ${szin}cc)`, border: `3px solid ${megvan ? szin : latszik ? `${szin}50` : szin}`, boxShadow: megvan ? `0 4px 12px ${szin}30` : '0 2px 8px rgba(0,0,0,0.1)'}}>
              {latszik ? <KepVagyEmoji ertek={k.emoji} meret={210} /> : <span style={{fontSize: '110px', color: 'white'}}>❓</span>}
            </div>
          );
        })}
      </div>
      {megtalalt.length === feladat.kepek.length && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '22px', fontWeight: '900', color: '#1e7e34', margin: 0}}>{feladat.visszajelzes_jo} 🎉</p>
        </div>
      )}
    </div>
  );
}

// ============ VÁLOGATÓ FELADAT ============
function ValogatoFeladat({ feladat, onKesz, lecke }) {
  const [elemek, setElemek] = useState(feladat.elemek.map(e => ({ ...e, elhelyezve: false, celKategoria: null })));
  const [dragolt, setDragolt] = useState(null);
  const [ellenorzott, setEllenorzott] = useState(false);
  const [helyes, setHelyes] = useState(false);
  const szin = lecke?.szin || '#4d96ff';

  // Nagy, egyértelmű szimbólum minden dobozhoz: ha a kategória saját
  // szimbólumot ad meg (pl. házikó egy semleges kategóriánál), azt
  // használjuk; egyébként piros doboznál ✕, zöldnél ✓, máskülönben a
  // felirat emojija.
  const nagySzimbolum = (kat) => {
    if (kat.szimbolum) return kat.szimbolum;
    if (kat.szin === '#ea4335') return '✕';
    if (kat.szin === '#34a853' || kat.szin === '#6bcb77') return '✓';
    return kat.felirat.split(' ')[0];
  };

  const onDrop = (kat) => {
    if (dragolt === null) return;
    const uj = [...elemek];
    uj[dragolt] = { ...uj[dragolt], elhelyezve: true, celKategoria: kat };
    setElemek(uj); setDragolt(null);
  };
  const ellenorzes = () => { setHelyes(elemek.every(e => e.elhelyezve && e.celKategoria === e.kategoria)); setEllenorzott(true); };
  const ujra = () => { setElemek(feladat.elemek.map(e => ({ ...e, elhelyezve: false, celKategoria: null }))); setEllenorzott(false); setHelyes(false); };
  const elhelyezetlen = elemek.filter(e => !e.elhelyezve);

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '17px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{feladat.kerdes}</p>
      </div>
      <div style={{background: 'var(--bg-card)', borderRadius: '20px', padding: '1.25rem', border: '2px dashed #e0e0e0', minHeight: '100px', display: 'flex', flexWrap: 'wrap', gap: '14px', justifyContent: 'center', alignItems: 'center'}}>
        {elhelyezetlen.map((e) => {
          const idx = elemek.indexOf(e);
          return (
            <div key={idx} draggable onDragStart={() => setDragolt(idx)} className="lk-btn"
              style={{background: `${szin}15`, border: `2px solid ${szin}40`, borderRadius: '18px', padding: '10px 20px 10px 10px', cursor: 'grab', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '14px', userSelect: 'none'}}>
              <KepVagyEmoji ertek={e.kep} meret={185} />
              <span style={{fontSize: '20px', fontWeight: '800', color: 'var(--text-primary)'}}>{e.felirat}</span>
            </div>
          );
        })}
        {elhelyezetlen.length === 0 && <p style={{color: '#34a853', fontWeight: '800', fontSize: '17px', margin: 0}}>✅ Minden elem elhelyezve!</p>}
      </div>
      <div style={{display: 'grid', gridTemplateColumns: `repeat(${feladat.kategoriak.length}, 1fr)`, gap: '1rem'}}>
        {feladat.kategoriak.map(kat => (
          <div key={kat.id} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(kat.id)}
            style={{position: 'relative', background: `${kat.szin}18`, border: `4px solid ${kat.szin}`, borderRadius: '24px', padding: '1rem', minHeight: '170px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'}}>
            <span style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: '110px', fontWeight: '900', color: kat.szin, opacity: 0.22, lineHeight: 1, userSelect: 'none', pointerEvents: 'none'}}>
              {nagySzimbolum(kat)}
            </span>
            <div style={{position: 'relative', display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', zIndex: 1}}>
              {elemek.filter(e => e.elhelyezve && e.celKategoria === kat.id).map((e) => {
                const jo = ellenorzott && e.kategoria === e.celKategoria;
                return (
                  <div key={elemek.indexOf(e)} style={{background: ellenorzott ? (jo ? '#e8f5e9' : '#fce8e6') : 'white', border: `2px solid ${ellenorzott ? (jo ? '#34a853' : '#ea4335') : '#ddd'}`, borderRadius: '14px', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', position: 'relative'}}>
                    <KepVagyEmoji ertek={e.kep} meret={100} />
                    {ellenorzott && <span style={{position: 'absolute', bottom: '-6px', right: '-6px', fontSize: '14px'}}>{jo ? '✅' : '❌'}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {ellenorzott && (
        <div style={{background: helyes ? '#e8f5e9' : '#fce8e6', border: `2px solid ${helyes ? '#34a853' : '#ea4335'}`, borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: helyes ? '#1e7e34' : '#c62828', margin: 0}}>{helyes ? feladat.visszajelzes_jo : feladat.visszajelzes_rossz}</p>
        </div>
      )}
      <div style={{display: 'flex', justifyContent: 'center'}}>
        {!ellenorzott ? (
          <button onClick={ellenorzes} disabled={elhelyezetlen.length > 0} className="lk-btn"
            style={{padding: '16px 40px', background: elhelyezetlen.length > 0 ? '#f0f0f0' : `linear-gradient(135deg, ${szin}, ${szin}cc)`, color: elhelyezetlen.length > 0 ? '#bbb' : 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: elhelyezetlen.length > 0 ? 'not-allowed' : 'pointer'}}>
            ✅ Ellenőrzés
          </button>
        ) : helyes ? (
          <button onClick={onKesz} className="lk-btn" style={{padding: '16px 40px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: 'pointer'}}>Tovább! 🚀</button>
        ) : (
          <button onClick={ujra} className="lk-btn" style={{padding: '16px 40px', background: 'linear-gradient(135deg, #f9ab00, #ff6d00)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: 'pointer'}}>🔄 Próbáld újra!</button>
        )}
      </div>
    </div>
  );
}

// ============ IGAZ VAGY HAMIS MÉRLEG ============
function IgazHamisMerleg({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [index, setIndex] = useState(0);
  const [valasz, setValasz] = useState(null);
  const [hibak, setHibak] = useState(0);
  const [billen, setBillen] = useState(0); // -1 bal, 1 jobb, 0 egyenes

  const akt = feladat.allitasok[index];
  const utolso = index === feladat.allitasok.length - 1;
  const helyesE = valasz !== null && valasz === akt.igaz;

  const valaszt = (tipp) => {
    if (valasz !== null) return;
    setValasz(tipp);
    setBillen(tipp === akt.igaz ? -1 : 1);
    if (tipp !== akt.igaz) setHibak(h => h + 1);
  };

  const tovabb = () => {
    setBillen(0);
    if (!utolso) { setIndex(i => i + 1); setValasz(null); }
    else onKesz();
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '17px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{index + 1}/{feladat.allitasok.length} — Igaz vagy hamis?</p>
      </div>

      <div style={{textAlign: 'center', fontSize: '90px', transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)', transform: `rotate(${billen * 12}deg)`}}>⚖️</div>

      <div style={{background: `${szin}12`, border: `3px solid ${szin}40`, borderRadius: '24px', padding: '2rem', textAlign: 'center'}}>
        <p style={{fontSize: '26px', fontWeight: '800', color: '#2d3748', margin: 0, lineHeight: 1.5}}>{akt.szoveg}</p>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
        <button onClick={() => valaszt(true)} className="lk-btn" disabled={valasz !== null}
          style={{padding: '2rem', background: valasz === true ? (helyesE ? '#e8f5e9' : '#fce8e6') : 'white', border: `4px solid ${valasz === true ? (helyesE ? '#34a853' : '#ea4335') : '#34a853'}`, borderRadius: '22px', cursor: valasz !== null ? 'default' : 'pointer', fontSize: '54px'}}>
          ✅
        </button>
        <button onClick={() => valaszt(false)} className="lk-btn" disabled={valasz !== null}
          style={{padding: '2rem', background: valasz === false ? (helyesE ? '#e8f5e9' : '#fce8e6') : 'white', border: `4px solid ${valasz === false ? (helyesE ? '#34a853' : '#ea4335') : '#ea4335'}`, borderRadius: '22px', cursor: valasz !== null ? 'default' : 'pointer', fontSize: '54px'}}>
          ❌
        </button>
      </div>

      {valasz !== null && (
        <div style={{background: helyesE ? '#e8f5e9' : '#fce8e6', border: `2px solid ${helyesE ? '#34a853' : '#ea4335'}`, borderRadius: '20px', padding: '1.25rem', textAlign: 'center'}}>
          <p style={{fontSize: '18px', fontWeight: '800', color: helyesE ? '#1e7e34' : '#c62828', margin: '0 0 4px'}}>{helyesE ? '✅ Pontosan!' : '❌ Nem egészen!'}</p>
          <p style={{fontSize: '16px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{akt.magyarazat}</p>
          <button onClick={tovabb} className="lk-btn" style={{marginTop: '1rem', padding: '12px 32px', background: `linear-gradient(135deg, ${szin}, ${szin}cc)`, color: 'white', border: 'none', borderRadius: '50px', fontSize: '16px', fontWeight: '800', cursor: 'pointer'}}>
            {utolso ? 'Tovább! 🚀' : 'Következő →'}
          </button>
        </div>
      )}
    </div>
  );
}

// ============ VÁLASZD KI! (általános, egy helyes válasz) ============
function ValaszdKi({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const korok = feladat.korok || [{ kerdes: feladat.kerdes, promptEmoji: feladat.promptEmoji, promptArnyek: feladat.promptArnyek, opciok: feladat.opciok }];
  const [korIndex, setKorIndex] = useState(0);
  const [kivalasztott, setKivalasztott] = useState(null);
  const [hiba, setHiba] = useState(false);
  const aktKor = korok[korIndex];

  const valaszt = (opt) => {
    if (kivalasztott) return;
    if (opt.helyes) {
      setKivalasztott(opt);
      setTimeout(() => {
        if (korIndex + 1 < korok.length) {
          setKorIndex(k => k + 1);
          setKivalasztott(null);
        } else {
          onKesz();
        }
      }, 1100);
    } else {
      setHiba(opt.emoji);
      setTimeout(() => setHiba(false), 700);
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '22px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{aktKor.kerdes}</p>
        {korok.length > 1 && <p style={{fontSize: '15px', color: szin, margin: '8px 0 0', fontWeight: '800'}}>Kör: {korIndex + 1}/{korok.length}</p>}
      </div>
      {aktKor.promptEmoji && (
        <div style={{textAlign: 'center', filter: aktKor.promptArnyek ? 'brightness(0) opacity(0.3)' : 'none'}}>
          <KepVagyEmoji ertek={aktKor.promptEmoji} meret={320} />
        </div>
      )}
      <div style={{display: 'grid', gridTemplateColumns: `repeat(${Math.min(aktKor.opciok.length, 2)}, 1fr)`, gap: '1.25rem'}}>
        {aktKor.opciok.map((o, i) => (
          <button key={i} onClick={() => valaszt(o)} className="lk-btn"
            style={{
              padding: '2.5rem 1.25rem', borderRadius: '28px', cursor: kivalasztott ? 'default' : 'pointer',
              background: kivalasztott === o ? '#e8f5e9' : hiba === o.emoji ? '#fce8e6' : 'white',
              border: `4px solid ${kivalasztott === o ? '#34a853' : hiba === o.emoji ? '#ea4335' : `${szin}40`}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px',
            }}>
            <KepVagyEmoji ertek={o.emoji} meret={230} />
            {o.felirat && <span style={{fontSize: '19px', fontWeight: '800', color: 'var(--text-primary)'}}>{o.felirat}</span>}
          </button>
        ))}
      </div>
      {kivalasztott && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.25rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: '#1e7e34', margin: 0}}>✅ Pontosan!</p>
        </div>
      )}
    </div>
  );
}

// ============ MI NEM ILLIK IDE? ============
function MiNemIllik({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const korok = feladat.korok || [{ elemek: feladat.elemek, masikIndex: feladat.masikIndex }];
  const [korIndex, setKorIndex] = useState(0);
  const [kivalasztott, setKivalasztott] = useState(null);
  const [hiba, setHiba] = useState(null);
  const aktKor = korok[korIndex];

  const kattint = (i) => {
    if (kivalasztott !== null) return;
    if (i === aktKor.masikIndex) {
      setKivalasztott(i);
      setTimeout(() => {
        if (korIndex + 1 < korok.length) { setKorIndex(k => k + 1); setKivalasztott(null); }
        else onKesz();
      }, 1200);
    } else {
      setHiba(i);
      setTimeout(() => setHiba(null), 700);
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '22px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{feladat.kerdes}</p>
        {korok.length > 1 && <p style={{fontSize: '15px', color: szin, margin: '8px 0 0', fontWeight: '800'}}>Kör: {korIndex + 1}/{korok.length}</p>}
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.1rem'}}>
        {aktKor.elemek.map((emoji, i) => (
          <button key={i} onClick={() => kattint(i)} className="lk-btn"
            style={{
              padding: '2.75rem 1.25rem', borderRadius: '28px', cursor: kivalasztott !== null ? 'default' : 'pointer',
              background: kivalasztott === i ? '#e8f5e9' : hiba === i ? '#fce8e6' : 'white',
              border: `4px solid ${kivalasztott === i ? '#34a853' : hiba === i ? '#ea4335' : `${szin}40`}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <KepVagyEmoji ertek={emoji} meret={220} />
          </button>
        ))}
      </div>
      {kivalasztott !== null && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.25rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: '#1e7e34', margin: 0}}>✅ Pontosan, ez nem oda illik!</p>
        </div>
      )}
    </div>
  );
}

// ============ PÁROSÍTÓ JÁTÉK (két oszlop összekötése) ============
function ParositoJatek({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const jobbOldal = useState(() => feladat.elemek.map((e, i) => ({ emoji: e, i })).sort(() => Math.random() - 0.5))[0];
  const [balKivalasztva, setBalKivalasztva] = useState(null);
  const [megoldott, setMegoldott] = useState([]);

  const balKattint = (i) => { if (!megoldott.includes(i)) setBalKivalasztva(i); };
  const jobbKattint = (jobbElem) => {
    if (balKivalasztva === null || megoldott.includes(balKivalasztva)) return;
    if (jobbElem.i === balKivalasztva) {
      const uj = [...megoldott, balKivalasztva];
      setMegoldott(uj);
      setBalKivalasztva(null);
      if (uj.length === feladat.elemek.length) setTimeout(onKesz, 1000);
    } else {
      setBalKivalasztva(null);
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '22px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{feladat.kerdes}</p>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
        <div style={{display: 'flex', flexDirection: 'column', gap: '0.9rem'}}>
          {feladat.elemek.map((emoji, i) => (
            <button key={i} onClick={() => balKattint(i)} className="lk-btn"
              style={{padding: '1.9rem', borderRadius: '24px', cursor: megoldott.includes(i) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: megoldott.includes(i) ? '#e8f5e9' : balKivalasztva === i ? `${szin}25` : 'white',
                border: `3px solid ${megoldott.includes(i) ? '#34a853' : balKivalasztva === i ? szin : '#e0e0e0'}`}}>
              <KepVagyEmoji ertek={emoji} meret={185} />
            </button>
          ))}
        </div>
        <div style={{display: 'flex', flexDirection: 'column', gap: '0.9rem'}}>
          {jobbOldal.map((jobbElem, idx) => (
            <button key={idx} onClick={() => jobbKattint(jobbElem)} className="lk-btn"
              style={{padding: '1.9rem', borderRadius: '24px', cursor: megoldott.includes(jobbElem.i) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: megoldott.includes(jobbElem.i) ? '#e8f5e9' : 'white',
                border: `3px solid ${megoldott.includes(jobbElem.i) ? '#34a853' : '#e0e0e0'}`}}>
              <KepVagyEmoji ertek={jobbElem.emoji} meret={185} />
            </button>
          ))}
        </div>
      </div>
      {megoldott.length === feladat.elemek.length && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.25rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: '#1e7e34', margin: 0}}>🎉 Minden pár megvan!</p>
        </div>
      )}
    </div>
  );
}

// ============ KERESD MEG! (rejtett tárgy rácsban) ============
function KeresdMeg({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const korok = feladat.korok || [{ kerdes: feladat.kerdes, racs: feladat.racs }];
  const [korIndex, setKorIndex] = useState(0);
  const [megtalalt, setMegtalalt] = useState([]);
  const aktKor = korok[korIndex];
  const celDarab = aktKor.racs.filter(r => r.cel).length;

  const kattint = (i) => {
    if (megtalalt.includes(i) || !aktKor.racs[i].cel) return;
    const uj = [...megtalalt, i];
    setMegtalalt(uj);
    if (uj.length === celDarab) {
      setTimeout(() => {
        if (korIndex + 1 < korok.length) { setKorIndex(k => k + 1); setMegtalalt([]); }
        else onKesz();
      }, 900);
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '22px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{aktKor.kerdes}</p>
        <p style={{fontSize: '15px', color: szin, margin: '8px 0 0', fontWeight: '800'}}>
          {megtalalt.length}/{celDarab} megtalálva {korok.length > 1 && `· Kör: ${korIndex + 1}/${korok.length}`}
        </p>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.85rem'}}>
        {aktKor.racs.map((elem, i) => {
          const kesz = megtalalt.includes(i);
          return (
            <button key={i} onClick={() => kattint(i)} className="lk-btn"
              style={{height: '180px', borderRadius: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: kesz ? '#e8f5e9' : 'white', border: `3px solid ${kesz ? '#34a853' : '#e0e0e0'}`}}>
              <KepVagyEmoji ertek={elem.emoji} meret={175} />
            </button>
          );
        })}
      </div>
      {megtalalt.length === celDarab && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.25rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: '#1e7e34', margin: 0}}>🎉 Mindet megtaláltad!</p>
        </div>
      )}
    </div>
  );
}

// ============ SORBA RENDEZÉS (koppints a helyes sorrendben) ============
function SorbaRendezes({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const keverve = useState(() => feladat.sorrend.map((e, i) => ({ emoji: e, i })).sort(() => Math.random() - 0.5))[0];
  const [kovetkezo, setKovetkezo] = useState(0);
  const [hiba, setHiba] = useState(null);
  const [kesz, setKesz] = useState([]);

  const kattint = (elem) => {
    if (elem.i === kovetkezo) {
      const uj = [...kesz, elem.i];
      setKesz(uj);
      if (kovetkezo + 1 === feladat.sorrend.length) setTimeout(onKesz, 900);
      setKovetkezo(k => k + 1);
    } else {
      setHiba(elem.i);
      setTimeout(() => setHiba(null), 500);
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '22px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{feladat.kerdes}</p>
      </div>
      <div style={{display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap'}}>
        {feladat.sorrend.map((_, i) => (
          <div key={i} style={{width: '58px', height: '58px', borderRadius: '14px', background: kesz.includes(i) ? '#e8f5e9' : '#f0f0f0', border: `2px solid ${kesz.includes(i) ? '#34a853' : '#ddd'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px'}}>
            {kesz.includes(i) ? <KepVagyEmoji ertek={feladat.sorrend[i]} meret={110} /> : (i + 1)}
          </div>
        ))}
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.1rem'}}>
        {keverve.map((elem, idx) => (
          <button key={idx} onClick={() => kattint(elem)} className="lk-btn" disabled={kesz.includes(elem.i)}
            style={{padding: '2.25rem', borderRadius: '26px', cursor: kesz.includes(elem.i) ? 'default' : 'pointer',
              opacity: kesz.includes(elem.i) ? 0.3 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: hiba === elem.i ? '#fce8e6' : 'white', border: `3px solid ${hiba === elem.i ? '#ea4335' : `${szin}40`}`}}>
            <KepVagyEmoji ertek={elem.emoji} meret={190} />
          </button>
        ))}
      </div>
    </div>
  );
}

// ============ KIRAKÓS JÁTÉK (csempék felfedése sorrendben) ============
function KirakosJatek({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const darab = feladat.darabszam;
  const sorrend = useState(() => Array.from({length: darab}, (_, i) => i).sort(() => Math.random() - 0.5))[0];
  const [felfedve, setFelfedve] = useState([]);
  const [kepHiba, setKepHiba] = useState(false);
  const kovetkezo = felfedve.length;

  const kattint = (poz) => {
    if (sorrend[poz] !== kovetkezo) return;
    const uj = [...felfedve, poz];
    setFelfedve(uj);
    if (uj.length === darab) setTimeout(onKesz, 1200);
  };

  const oszlopok = darab === 8 ? 4 : 4;

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '18px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>Koppints sorban 1-től {darab}-ig, hogy felfedd a képet!</p>
      </div>
      <div style={{position: 'relative', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.15)'}}>
        <div style={{position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${szin}30, ${szin}10)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '120px'}}>
          {feladat.kep && !kepHiba ? (
            <img src={feladat.kep} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} onError={() => setKepHiba(true)} />
          ) : <span>{feladat.kepEmoji}</span>}
        </div>
        <div style={{position: 'relative', display: 'grid', gridTemplateColumns: `repeat(${oszlopok}, 1fr)`, gap: '4px', padding: '4px'}}>
          {sorrend.map((sorszam, poz) => {
            const kesz = felfedve.includes(poz);
            return (
              <button key={poz} onClick={() => kattint(poz)} className="lk-btn"
                style={{height: '260px', border: 'none', cursor: kesz ? 'default' : 'pointer',
                  background: kesz ? 'transparent' : `linear-gradient(135deg, ${szin}, ${szin}cc)`,
                  color: 'white', fontSize: '52px', fontWeight: '900', textShadow: '0 2px 6px rgba(0,0,0,0.35)',
                  boxShadow: kesz ? 'none' : '0 2px 8px rgba(0,0,0,0.15)', transition: 'all 0.3s'}}>
                {kesz ? '' : sorszam + 1}
              </button>
            );
          })}
        </div>
      </div>
      {felfedve.length === darab && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '22px', fontWeight: '900', color: '#1e7e34', margin: 0}}>🎉 Kirakva! Szuper voltál!</p>
        </div>
      )}
    </div>
  );
}

// ============ SZÍNEZŐ ============
function Szinezo({ feladat, onKesz, lecke }) {
  const PALETTA = ['#ff6b9d', '#ffd93d', '#6bcb77', '#4d96ff', '#c77dff', '#ff6b35', '#8d6e63', '#1a1a2e'];
  const [aktivSzin, setAktivSzin] = useState(PALETTA[0]);
  const [ecsetMeret, setEcsetMeret] = useState(28);
  const vaszonRef = useRef(null);
  const fest = useRef(false);
  const SZELESSEG = 900, MAGASSAG = 700;

  useEffect(() => {
    const vaszon = vaszonRef.current;
    if (!vaszon) return;
    const ctx = vaszon.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, SZELESSEG, MAGASSAG);
  }, []);

  const pozicio = (e) => {
    const vaszon = vaszonRef.current;
    const rect = vaszon.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return {
      x: (p.clientX - rect.left) * (SZELESSEG / rect.width),
      y: (p.clientY - rect.top) * (MAGASSAG / rect.height),
    };
  };

  const pontFestes = (e) => {
    const ctx = vaszonRef.current.getContext('2d');
    const { x, y } = pozicio(e);
    ctx.fillStyle = aktivSzin;
    ctx.beginPath();
    ctx.arc(x, y, ecsetMeret, 0, Math.PI * 2);
    ctx.fill();
  };

  const kezdes = (e) => { fest.current = true; pontFestes(e); };
  const mozgas = (e) => { if (fest.current) { e.preventDefault(); pontFestes(e); } };
  const vege = () => { fest.current = false; };

  const ujra = () => {
    const ctx = vaszonRef.current.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, SZELESSEG, MAGASSAG);
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '18px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>Válassz színt és ecsetméretet, majd húzd az ujjad/egeret a képen!</p>
      </div>
      <div style={{display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap'}}>
        {PALETTA.map(szin => (
          <button key={szin} onClick={() => setAktivSzin(szin)}
            style={{width: '60px', height: '60px', borderRadius: '50%', background: szin, cursor: 'pointer', border: aktivSzin === szin ? '4px solid #1a1a2e' : '4px solid white', boxShadow: aktivSzin === szin ? '0 0 0 3px ' + szin + '55' : '0 2px 8px rgba(0,0,0,0.2)'}}/>
        ))}
      </div>
      <div style={{display: 'flex', alignItems: 'center', gap: '1.25rem', justifyContent: 'center', background: 'var(--bg-card)', borderRadius: '20px', padding: '1rem 1.5rem', border: '2px solid var(--border-color)', maxWidth: '480px', margin: '0 auto', width: '100%'}}>
        <span style={{fontSize: '15px', fontWeight: '800', color: 'var(--text-secondary)', whiteSpace: 'nowrap'}}>✏️ Ecset:</span>
        <input type="range" min="8" max="60" value={ecsetMeret} onChange={e => setEcsetMeret(Number(e.target.value))}
          style={{flex: 1, height: '8px', accentColor: lecke?.szin || '#4d96ff', cursor: 'pointer'}} />
        <span style={{width: `${Math.min(ecsetMeret, 44)}px`, height: `${Math.min(ecsetMeret, 44)}px`, borderRadius: '50%', background: aktivSzin, flexShrink: 0, border: '2px solid var(--border-color-strong)'}} />
      </div>
      <div style={{position: 'relative', maxWidth: '900px', width: '100%', margin: '0 auto', borderRadius: '32px', overflow: 'hidden', border: '3px solid var(--border-color)', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', background: 'var(--bg-card)'}}>
        <canvas
          ref={vaszonRef}
          width={SZELESSEG}
          height={MAGASSAG}
          onMouseDown={kezdes} onMouseMove={mozgas} onMouseUp={vege} onMouseLeave={vege}
          onTouchStart={kezdes} onTouchMove={mozgas} onTouchEnd={vege}
          style={{width: '100%', height: 'auto', display: 'block', touchAction: 'none', cursor: 'crosshair'}}
        />
        <img src={feladat.kep} alt="" draggable={false}
          style={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply', pointerEvents: 'none'}} />
      </div>
      <div style={{display: 'flex', gap: '1rem', justifyContent: 'center'}}>
        <button onClick={ujra} className="lk-btn" style={{padding: '14px 28px', background: 'var(--bg-card)', color: 'var(--text-muted)', border: '2px solid var(--border-color-strong)', borderRadius: '50px', fontSize: '16px', fontWeight: '800', cursor: 'pointer'}}>
          🔄 Törlés
        </button>
        <button onClick={onKesz} className="lk-btn" style={{padding: '16px 40px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: 'pointer'}}>
          Kész vagyok! 🎨
        </button>
      </div>
    </div>
  );
}

// ============ ÖSSZEKÖTŐ PONTOK (körberajzolás) ============
function OsszekotoPontok({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [reached, setReached] = useState(0);
  const [kurzor, setKurzor] = useState(null);
  const huzas = useRef(false);
  const svgRef = useRef(null);
  const pontok = feladat.pontok;
  // Zárt alaknál a vonal a végén visszatér a kezdőpontra.
  const utvonal = feladat.zart === false ? pontok : [...pontok, pontok[0]];
  const TURES = 45; // hány pixelnyi eltérést engedünk a következő ponttól

  const pozicio = (e) => {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return {
      x: (p.clientX - rect.left) * (400 / rect.width),
      y: (p.clientY - rect.top) * (400 / rect.height),
    };
  };

  const halad = (e) => {
    const pos = pozicio(e);
    setKurzor(pos);
    if (reached >= utvonal.length) return;
    const cel = utvonal[reached];
    const tav = Math.hypot(pos.x - cel.x, pos.y - cel.y);
    if (tav < TURES) {
      const uj = reached + 1;
      setReached(uj);
      if (uj === utvonal.length) {
        huzas.current = false;
        setTimeout(onKesz, 1100);
      }
    }
  };

  const kezdes = (e) => {
    if (reached >= utvonal.length) return;
    huzas.current = true;
    halad(e);
  };
  const mozgas = (e) => { if (huzas.current) { e.preventDefault(); halad(e); } };
  const vege = () => { huzas.current = false; };

  const kesz_pontok = utvonal.slice(0, reached);
  const vonalPontok = kesz_pontok.map(p => `${p.x},${p.y}`).join(' ');
  const eloVonal = reached > 0 && reached < utvonal.length && kurzor
    ? `${utvonal[reached - 1].x},${utvonal[reached - 1].y} ${kurzor.x},${kurzor.y}` : null;

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '18px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>Nyomd le az ujjad/egeret az elején, és húzd végig a pontokon, hogy megrajzold: {feladat.celNev}!</p>
      </div>
      <div style={{background: 'var(--bg-card-alt)', borderRadius: '28px', border: '3px solid var(--border-color)', padding: '1rem'}}>
        <svg ref={svgRef} viewBox="0 0 400 400" style={{width: '100%', maxWidth: '520px', display: 'block', margin: '0 auto', touchAction: 'none', cursor: 'crosshair'}}
          onMouseDown={kezdes} onMouseMove={mozgas} onMouseUp={vege} onMouseLeave={vege}
          onTouchStart={kezdes} onTouchMove={mozgas} onTouchEnd={vege}>
          {feladat.celEmoji && (
            <text x="200" y="230" textAnchor="middle" fontSize="230" opacity="0.16">{feladat.celEmoji}</text>
          )}
          {/* halvány vezetővonal a teljes alak mentén */}
          <polyline points={utvonal.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke={`${szin}30`} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
          {vonalPontok && (
            <polyline points={vonalPontok} fill="none" stroke={szin} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
          )}
          {eloVonal && (
            <polyline points={eloVonal} fill="none" stroke={szin} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
          )}
          {pontok.map((p, i) => {
            const van_kesz = i < reached;
            const aktiv = i === reached;
            return (
              <circle key={i} cx={p.x} cy={p.y} r={aktiv ? 16 : 10}
                fill={van_kesz ? '#34a853' : aktiv ? szin : 'white'}
                stroke={van_kesz ? '#34a853' : szin} strokeWidth="3" />
            );
          })}
        </svg>
      </div>
      {reached === utvonal.length && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.25rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: '#1e7e34', margin: 0}}>🎉 Szuper, körberajzoltad!</p>
        </div>
      )}
    </div>
  );
}

// ============ KÖNYV ============
function KonyvOlvaso({ konyv, lecke, onKesz }) {
  const [aktOldal, setAktOldal] = useState(0);
  const [animacio, setAnimacio] = useState('');
  const [animFut, setAnimFut] = useState(false);
  const aktualOldal = konyv.oldalak[aktOldal];
  const utolsoOldal = aktOldal === konyv.oldalak.length - 1;

  const lapoz = (irany) => {
    if (animFut) return;
    const ujIndex = irany === 'elore' ? aktOldal + 1 : aktOldal - 1;
    if (ujIndex < 0 || ujIndex >= konyv.oldalak.length) return;
    setAnimFut(true);
    setAnimacio(irany === 'elore' ? 'lap-ki-bal' : 'lap-ki-jobb');
    setTimeout(() => {
      setAktOldal(ujIndex);
      setAnimacio('lap-be');
      setTimeout(() => { setAnimacio(''); setAnimFut(false); }, 500);
    }, 450);
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem'}}>
      <div style={{textAlign: 'center', width: '100%', maxWidth: '880px'}}>
        <div style={{display: 'inline-flex', alignItems: 'center', gap: '10px', background: `${lecke.szin}20`, border: `2px solid ${lecke.szin}50`, borderRadius: '50px', padding: '12px 28px', fontSize: '18px', fontWeight: '800', color: lecke.szin, marginBottom: '1.25rem'}}>
          📖 {konyv.cim}
        </div>
        <div style={{display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center'}}>
          {konyv.oldalak.map((_, i) => (
            <div key={i} onClick={() => { if (i > aktOldal) lapoz('elore'); else if (i < aktOldal) lapoz('vissza'); }}
              style={{width: i === aktOldal ? '42px' : '16px', height: '16px', borderRadius: '8px', background: i <= aktOldal ? lecke.szin : '#e0e0e0', cursor: 'pointer', transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)', boxShadow: i === aktOldal ? `0 2px 8px ${lecke.szin}50` : 'none'}}/>
          ))}
          <span style={{fontSize: '15px', color: '#aaa', fontWeight: '700', marginLeft: '10px'}}>{aktOldal + 1}/{konyv.oldalak.length}</span>
        </div>
      </div>
      <div style={{width: '100%', maxWidth: '880px', filter: `drop-shadow(0 28px 56px ${lecke.szin}30)`, animation: 'konyvLebeg 4s ease-in-out infinite'}}>
        <div className={animacio} style={{background: aktualOldal.haatter, borderRadius: '36px', minHeight: '500px', border: `3px solid ${lecke.szin}30`, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column', boxShadow: `0 2px 0 ${lecke.szin}30, 6px 6px 0 ${lecke.szin}18, inset 0 1px 0 rgba(255,255,255,0.8)`}}>
          <div style={{position: 'absolute', left: 0, top: 0, bottom: 0, width: '14px', background: `linear-gradient(180deg, ${lecke.szin}ee, ${lecke.szin}99)`, boxShadow: `2px 0 10px ${lecke.szin}35`}}/>
          <div style={{position: 'absolute', right: 0, top: 0, bottom: 0, width: '5px', background: 'linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.1))'}}/>

          <div style={{flex: 1, padding: '3rem 3.5rem 1.75rem 4.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2rem'}}>
            <div style={{fontSize: '92px', lineHeight: 1, animation: 'pulzal 3s ease-in-out infinite', textAlign: 'center', filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.12))'}}>
              {aktualOldal.illusztracio}
            </div>
            <p style={{fontSize: '21px', color: '#2d3748', lineHeight: '1.75', textAlign: 'center', fontWeight: '700', margin: 0, maxWidth: '620px'}}>
              {aktualOldal.szoveg}
            </p>
            {aktualOldal.szabaly && (
              <div style={{background: `linear-gradient(135deg, ${lecke.szin}25, ${lecke.szin}12)`, border: `3px solid ${lecke.szin}`, borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', width: '100%', animation: 'csillog 2.5s ease-in-out infinite', boxShadow: `0 4px 16px ${lecke.szin}25`}}>
                <p style={{fontSize: '22px', fontWeight: '900', color: lecke.szin, margin: 0}}>⭐ {aktualOldal.szabaly}</p>
              </div>
            )}
          </div>

          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 3rem 2.25rem 4.5rem', borderTop: `1px solid ${lecke.szin}20`}}>
            <button onClick={() => lapoz('vissza')} disabled={aktOldal === 0 || animFut} className="lk-btn"
              style={{background: aktOldal === 0 ? 'transparent' : 'rgba(255,255,255,0.9)', border: `2px solid ${aktOldal === 0 ? '#e0e0e0' : lecke.szin}`, borderRadius: '50px', padding: '14px 30px', cursor: aktOldal === 0 ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: '800', color: aktOldal === 0 ? '#ccc' : lecke.szin, boxShadow: aktOldal === 0 ? 'none' : `0 2px 8px ${lecke.szin}20`}}>
              ← Előző
            </button>
            <div style={{fontSize: '48px'}}>{aktualOldal.ikon}</div>
            {!utolsoOldal ? (
              <button onClick={() => lapoz('elore')} disabled={animFut} className="lk-btn"
                style={{background: `linear-gradient(135deg, ${lecke.szin}, ${lecke.szin}cc)`, border: 'none', borderRadius: '50px', padding: '14px 30px', cursor: 'pointer', fontSize: '16px', fontWeight: '800', color: 'white', boxShadow: `0 4px 16px ${lecke.szin}45`}}>
                Következő →
              </button>
            ) : (
              <button onClick={onKesz} className="lk-btn"
                style={{background: 'linear-gradient(135deg, #34a853, #66bb6a)', border: 'none', borderRadius: '50px', padding: '14px 30px', cursor: 'pointer', fontSize: '16px', fontWeight: '800', color: 'white', boxShadow: '0 4px 16px rgba(52,168,83,0.45)'}}>
                Játékok! 🎮
              </button>
            )}
          </div>
        </div>
      </div>
      <p style={{fontSize: '15px', color: '#bbb', fontWeight: '700', textAlign: 'center', animation: 'csillog 2s ease-in-out infinite'}}>
        {utolsoOldal ? '🎉 Vége a mesének! Nyomd meg a Játékok gombot!' : '📖 Lapozz a nyilakkal vagy a pontokra kattintva!'}
      </p>
    </div>
  );
}

// ============ VIDEÓ LEJÁTSZÓ ============
function VideoLejatszo({ videoUrl, onVegig, lecke }) {
  const videoRef = useRef(null);
  const [lejatszas, setLejatszas] = useState(false);
  const [halad, setHalad] = useState(0);
  const [hossz, setHossz] = useState(0);
  const [vege, setVege] = useState(false);
  const szin = lecke?.szin || '#4d96ff';

  const play = () => { const v = videoRef.current; if (!v) return; lejatszas ? v.pause() : v.play(); };
  const formatIdo = (s) => !s || isNaN(s) ? '0:00' : `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`;

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
      <div style={{background: '#0a0e1a', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.3)', position: 'relative'}}>
        <video ref={videoRef} src={videoUrl} onPlay={() => setLejatszas(true)} onPause={() => setLejatszas(false)} onTimeUpdate={() => { const v = videoRef.current; if(v){setHalad(v.currentTime);setHossz(v.duration||0);}}} onLoadedMetadata={() => { const v = videoRef.current; if(v) setHossz(v.duration||0);}} onEnded={() => {setLejatszas(false);setVege(true);}} onClick={play} style={{width: '100%', display: 'block', maxHeight: '55vh', cursor: 'pointer'}}/>
        {!lejatszas && halad === 0 && (
          <div onClick={play} style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', cursor: 'pointer'}}>
            <div style={{width: '80px', height: '80px', borderRadius: '50%', background: 'var(--bg-card-transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <div style={{width: 0, height: 0, marginLeft: '8px', borderTop: '18px solid transparent', borderBottom: '18px solid transparent', borderLeft: `28px solid ${szin}`}}/>
            </div>
          </div>
        )}
        <div style={{background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', padding: '1.5rem 1.25rem 1rem', marginTop: '-56px', position: 'relative'}}>
          <div style={{height: '5px', background: 'rgba(255,255,255,0.2)', borderRadius: '3px', marginBottom: '0.75rem', overflow: 'hidden', cursor: 'pointer'}} onClick={(e) => { const v = videoRef.current; if (!v || !hossz) return; v.currentTime = ((e.clientX - e.currentTarget.getBoundingClientRect().left) / e.currentTarget.getBoundingClientRect().width) * hossz; }}>
            <div style={{height: '100%', width: `${hossz > 0 ? (halad/hossz)*100 : 0}%`, background: `linear-gradient(90deg, ${szin}, ${szin}88)`, borderRadius: '3px'}}/>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
            <button onClick={play} style={{background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', fontSize: '14px'}}>{lejatszas ? '⏸' : '▶'}</button>
            <span style={{color: 'var(--border-color-strong)', fontSize: '12px', fontWeight: '700'}}>{formatIdo(halad)} / {formatIdo(hossz)}</span>
            <div style={{flex: 1}}/>
            {vege && <span style={{color: '#6bcb77', fontSize: '13px', fontWeight: '800'}}>✓ Megnézve!</span>}
          </div>
        </div>
      </div>
      {!vege ? (
        <div style={{background: '#fff8e1', border: '2px solid #ffd93d', borderRadius: '18px', padding: '1rem', textAlign: 'center'}}>
          <p style={{fontSize: '17px', fontWeight: '800', color: '#7a5900', margin: 0}}>🎬 Nézd végig a videót!</p>
        </div>
      ) : (
        <button onClick={onVegig} className="lk-btn" style={{padding: '16px 40px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: 'pointer', alignSelf: 'center'}}>
          Tovább a játékokhoz! 🎮
        </button>
      )}
    </div>
  );
}

// ============ BUBORÉK JÁTÉK (2. lecke) ============
function BuborekJatek({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [pontok, setPontok] = useState(0);
  const [elet, setElet] = useState(3);
  const [buborekok, setBuborekok] = useState([]);
  const [kesz, setKesz] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [uzenet, setUzenet] = useState('');
  const idSzamlalo = useRef(0);
  const fut = useRef(true);

  useEffect(() => {
    const jok = feladat.gyujtheto || ['⭐'];
    const rosszak = feladat.veszelyek || ['⚠️'];
    const spawn = setInterval(() => {
      if (!fut.current) return;
      const jo = Math.random() > 0.35;
      idSzamlalo.current += 1;
      setBuborekok(prev => [...prev, {
        id: idSzamlalo.current,
        x: 8 + Math.random() * 80,
        emoji: jo ? jok[Math.floor(Math.random() * jok.length)] : rosszak[Math.floor(Math.random() * rosszak.length)],
        jo,
      }]);
    }, 1400);
    return () => clearInterval(spawn);
  }, [feladat]);

  const kattint = (b) => {
    if (!fut.current) return;
    setBuborekok(prev => prev.filter(x => x.id !== b.id));
    if (b.jo) {
      const uj = pontok + 1;
      setPontok(uj);
      if (uj >= 6) { fut.current = false; setKesz(true); }
    } else {
      const uj = elet - 1;
      setElet(uj);
      setUzenet('😱 Az nem jó volt!');
      setTimeout(() => setUzenet(''), 1000);
      if (uj <= 0) { fut.current = false; setGameOver(true); }
    }
  };

  const ujra = () => {
    fut.current = true; setPontok(0); setElet(3); setBuborekok([]); setKesz(false); setGameOver(false);
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.25rem 1.5rem', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)', margin: '0 0 4px'}}>{feladat.cim}</p>
        <p style={{fontSize: '16px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{feladat.kerdes}</p>
      </div>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-card)', borderRadius: '16px', padding: '0.75rem 1.25rem', border: '2px solid var(--border-color)'}}>
        <div style={{display: 'flex', gap: '4px'}}>{[0,1,2].map(i => <span key={i} style={{fontSize: '22px', opacity: i < elet ? 1 : 0.2}}>❤️</span>)}</div>
        <div style={{fontSize: '16px', fontWeight: '800', color: szin}}>⭐ {pontok} / 6</div>
      </div>
      <div style={{position: 'relative', height: '460px', borderRadius: '28px', overflow: 'hidden', background: 'linear-gradient(180deg, #cceeff, #eaffea)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)'}}>
        {buborekok.map(b => (
          <button key={b.id} onClick={() => kattint(b)}
            style={{
              position: 'absolute', left: `${b.x}%`, bottom: '-90px', width: '180px', height: '180px', borderRadius: '50%',
              background: 'var(--bg-card-transparent)', border: `4px solid ${b.jo ? '#34a853' : '#ea4335'}`, fontSize: '40px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              animation: 'buborekFel 13s linear forwards', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
            onAnimationEnd={() => setBuborekok(prev => prev.filter(x => x.id !== b.id))}>
            <KepVagyEmoji ertek={b.emoji} meret={150} />
          </button>
        ))}
        {uzenet && <div style={{position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.75)', color: 'white', padding: '8px 20px', borderRadius: '14px', fontWeight: '800', fontSize: '16px'}}>{uzenet}</div>}
        <style>{`@keyframes buborekFel { 0% { bottom: -70px; } 100% { bottom: 420px; } }`}</style>
      </div>
      <p style={{fontSize: '14px', color: '#999', textAlign: 'center', fontWeight: '700'}}>👆 Pukkantsd ki a jókat! Kerüld a rosszakat!</p>
      {kesz && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '22px', fontWeight: '900', color: '#1e7e34', margin: '0 0 1rem'}}>🎉 Szuper pukkantás!</p>
          <button onClick={onKesz} className="lk-btn" style={{padding: '14px 36px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '17px', fontWeight: '800', cursor: 'pointer'}}>Tovább! 🚀</button>
        </div>
      )}
      {gameOver && !kesz && (
        <div style={{background: '#fce8e6', border: '2px solid #ea4335', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: '#c62828', margin: '0 0 1rem'}}>😔 Próbáld újra!</p>
          <button onClick={ujra} className="lk-btn" style={{padding: '14px 36px', background: 'linear-gradient(135deg, #f9ab00, #ff6d00)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '17px', fontWeight: '800', cursor: 'pointer'}}>🔄 Újra!</button>
        </div>
      )}
    </div>
  );
}

// ============ KAPD EL JÁTÉK (3. lecke) ============
function KapdElJatek({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [kosarX, setKosarX] = useState(50);
  const [elemek, setElemek] = useState([]);
  const [pontok, setPontok] = useState(0);
  const [elet, setElet] = useState(3);
  const [kesz, setKesz] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [uzenet, setUzenet] = useState('');
  const idSzamlalo = useRef(0);
  const kosarXRef = useRef(50);
  const fut = useRef(true);

  useEffect(() => { kosarXRef.current = kosarX; }, [kosarX]);

  useEffect(() => {
    const jok = feladat.gyujtheto || ['⭐'];
    const rosszak = feladat.veszelyek || ['⚠️'];
    const spawn = setInterval(() => {
      if (!fut.current) return;
      const jo = Math.random() > 0.35;
      idSzamlalo.current += 1;
      setElemek(prev => [...prev, { id: idSzamlalo.current, x: 8 + Math.random() * 80, y: 0, emoji: jo ? jok[Math.floor(Math.random()*jok.length)] : rosszak[Math.floor(Math.random()*rosszak.length)], jo }]);
    }, 1300);
    const mozgas = setInterval(() => {
      if (!fut.current) return;
      setElemek(prev => prev.map(e => ({ ...e, y: e.y + 1.3 })).filter(e => {
        if (e.y >= 88) {
          if (Math.abs(e.x - kosarXRef.current) < 12) {
            if (e.jo) {
              setPontok(p => { const uj = p + 1; if (uj >= 6) { fut.current = false; setKesz(true); } return uj; });
            } else {
              setElet(l => { const uj = l - 1; setUzenet('😱 Jaj, az rossz volt!'); setTimeout(() => setUzenet(''), 900); if (uj <= 0) { fut.current = false; setGameOver(true); } return uj; });
            }
          }
          return false;
        }
        return true;
      }));
    }, 40);
    return () => { clearInterval(spawn); clearInterval(mozgas); };
  }, [feladat]);

  useEffect(() => {
    const kd = (e) => {
      if (e.key === 'ArrowLeft') setKosarX(x => Math.max(6, x - 6));
      if (e.key === 'ArrowRight') setKosarX(x => Math.min(94, x + 6));
    };
    window.addEventListener('keydown', kd);
    return () => window.removeEventListener('keydown', kd);
  }, []);

  const ujra = () => { fut.current = true; setPontok(0); setElet(3); setElemek([]); setKesz(false); setGameOver(false); };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.25rem 1.5rem', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)', margin: '0 0 4px'}}>{feladat.cim}</p>
        <p style={{fontSize: '16px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{feladat.kerdes}</p>
      </div>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-card)', borderRadius: '16px', padding: '0.75rem 1.25rem', border: '2px solid var(--border-color)'}}>
        <div style={{display: 'flex', gap: '4px'}}>{[0,1,2].map(i => <span key={i} style={{fontSize: '22px', opacity: i < elet ? 1 : 0.2}}>❤️</span>)}</div>
        <div style={{fontSize: '16px', fontWeight: '800', color: szin}}>⭐ {pontok} / 6</div>
      </div>
      <div style={{position: 'relative', height: '460px', borderRadius: '28px', overflow: 'hidden', background: 'linear-gradient(180deg, #fff6e0, #ffe8f0)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)'}}>
        {elemek.map(e => (
          <div key={e.id} style={{position: 'absolute', left: `${e.x}%`, top: `${e.y}%`, transform: 'translate(-50%,-50%)'}}><KepVagyEmoji ertek={e.emoji} meret={155} /></div>
        ))}
        <div style={{position: 'absolute', left: `${kosarX}%`, bottom: '4%', fontSize: '90px', transform: 'translateX(-50%)', transition: 'left 0.1s'}}>🧺</div>
        {uzenet && <div style={{position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.75)', color: 'white', padding: '8px 20px', borderRadius: '14px', fontWeight: '800', fontSize: '16px'}}>{uzenet}</div>}
      </div>
      <div style={{display: 'flex', justifyContent: 'center', gap: '1rem'}}>
        <button onTouchStart={e => { e.preventDefault(); setKosarX(x => Math.max(6, x - 8)); }} onMouseDown={() => setKosarX(x => Math.max(6, x - 8))} className="lk-btn"
          style={{width:'72px',height:'72px',background:`linear-gradient(135deg,${szin},${szin}cc)`,border:'none',borderRadius:'20px',fontSize:'30px',cursor:'pointer',color:'white'}}>⬅️</button>
        <button onTouchStart={e => { e.preventDefault(); setKosarX(x => Math.min(94, x + 8)); }} onMouseDown={() => setKosarX(x => Math.min(94, x + 8))} className="lk-btn"
          style={{width:'72px',height:'72px',background:`linear-gradient(135deg,${szin},${szin}cc)`,border:'none',borderRadius:'20px',fontSize:'30px',cursor:'pointer',color:'white'}}>➡️</button>
      </div>
      {kesz && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '22px', fontWeight: '900', color: '#1e7e34', margin: '0 0 1rem'}}>🎉 Ügyes kosár voltál!</p>
          <button onClick={onKesz} className="lk-btn" style={{padding: '14px 36px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '17px', fontWeight: '800', cursor: 'pointer'}}>Tovább! 🚀</button>
        </div>
      )}
      {gameOver && !kesz && (
        <div style={{background: '#fce8e6', border: '2px solid #ea4335', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: '#c62828', margin: '0 0 1rem'}}>😔 Próbáld újra!</p>
          <button onClick={ujra} className="lk-btn" style={{padding: '14px 36px', background: 'linear-gradient(135deg, #f9ab00, #ff6d00)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '17px', fontWeight: '800', cursor: 'pointer'}}>🔄 Újra!</button>
        </div>
      )}
    </div>
  );
}

// ============ VILLANÓ KÁRTYÁK (4. lecke) ============
function VillanoKartyak({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [kor, setKor] = useState(0);
  const [aktualis, setAktualis] = useState(null);
  const [valasz, setValasz] = useState(null);
  const [pontok, setPontok] = useState(0);
  const [kesz, setKesz] = useState(false);
  const CELSZAM = 8;

  const ujKor = () => {
    const jok = feladat.gyujtheto || ['⭐'];
    const rosszak = feladat.veszelyek || ['⚠️'];
    const jo = Math.random() > 0.5;
    setAktualis({ emoji: jo ? jok[Math.floor(Math.random()*jok.length)] : rosszak[Math.floor(Math.random()*rosszak.length)], jo });
    setValasz(null);
  };

  useEffect(() => { ujKor(); }, []);

  const tippel = (tippJo) => {
    if (valasz !== null || !aktualis) return;
    setValasz(tippJo);
    if (tippJo === aktualis.jo) setPontok(p => p + 1);
    setTimeout(() => {
      if (kor + 1 >= CELSZAM) setKesz(true);
      else { setKor(k => k + 1); ujKor(); }
    }, 900);
  };

  const helyesE = valasz !== null && valasz === aktualis?.jo;

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '17px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{feladat.kerdes}</p>
        <p style={{fontSize: '15px', color: szin, margin: '8px 0 0', fontWeight: '800'}}>Kör: {kor + 1}/{CELSZAM} &nbsp; ⭐ {pontok}</p>
      </div>
      {!kesz && aktualis && (
        <>
          <div style={{background: valasz === null ? 'white' : (helyesE ? '#e8f5e9' : '#fce8e6'), border: `3px solid ${valasz === null ? '#f0f0f0' : (helyesE ? '#34a853' : '#ea4335')}`, borderRadius: '28px', padding: '3rem', textAlign: 'center', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <span style={{fontSize: '110px', animation: valasz === null ? 'pulzal 0.8s ease-in-out infinite' : 'none'}}>{aktualis.emoji}</span>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
            <button onClick={() => tippel(true)} disabled={valasz !== null} className="lk-btn"
              style={{padding: '1.75rem', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '22px', fontSize: '46px', cursor: valasz !== null ? 'default' : 'pointer', opacity: valasz !== null && valasz !== true ? 0.5 : 1}}>
              ✅
            </button>
            <button onClick={() => tippel(false)} disabled={valasz !== null} className="lk-btn"
              style={{padding: '1.75rem', background: 'linear-gradient(135deg, #ea4335, #ff6d6d)', color: 'white', border: 'none', borderRadius: '22px', fontSize: '46px', cursor: valasz !== null ? 'default' : 'pointer', opacity: valasz !== null && valasz !== false ? 0.5 : 1}}>
              🚫
            </button>
          </div>
        </>
      )}
      {kesz && (
        <div style={{background: pontok >= 4 ? '#e8f5e9' : '#fff3e0', border: `2px solid ${pontok >= 4 ? '#34a853' : '#ffab00'}`, borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '22px', fontWeight: '900', color: pontok >= 4 ? '#1e7e34' : '#a86200', margin: '0 0 1rem'}}>
            {pontok >= 4 ? `🎉 Szuper! ${pontok}/${CELSZAM} pont!` : `Ügyes voltál! ${pontok}/${CELSZAM} pont`}
          </p>
          <button onClick={onKesz} className="lk-btn" style={{padding: '14px 36px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '17px', fontWeight: '800', cursor: 'pointer'}}>Tovább! 🚀</button>
        </div>
      )}
    </div>
  );
}

// ============ RÁCS VADÁSZAT (5. lecke) ============
function RacsVadaszat({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#4d96ff';
  const [cellak, setCellak] = useState(Array(9).fill(null));
  const [pontok, setPontok] = useState(0);
  const [elet, setElet] = useState(3);
  const [kesz, setKesz] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const fut = useRef(true);

  useEffect(() => {
    const jok = feladat.gyujtheto || ['⭐'];
    const rosszak = feladat.veszelyek || ['⚠️'];
    const idozito = setInterval(() => {
      if (!fut.current) return;
      setCellak(prev => {
        const uj = [...prev];
        const uresek = uj.map((v, i) => v === null ? i : -1).filter(i => i >= 0);
        if (uresek.length === 0) return uj;
        const cel = uresek[Math.floor(Math.random() * uresek.length)];
        const jo = Math.random() > 0.35;
        uj[cel] = { emoji: jo ? jok[Math.floor(Math.random()*jok.length)] : rosszak[Math.floor(Math.random()*rosszak.length)], jo };
        return uj;
      });
    }, 750);
    const eltuntetes = setInterval(() => {
      setCellak(prev => prev.map(c => (c && Math.random() > 0.6 ? null : c)));
    }, 900);
    return () => { clearInterval(idozito); clearInterval(eltuntetes); };
  }, [feladat]);

  const kattint = (i) => {
    const c = cellak[i];
    if (!c || !fut.current) return;
    setCellak(prev => { const u = [...prev]; u[i] = null; return u; });
    if (c.jo) {
      setPontok(p => { const uj = p + 1; if (uj >= 6) { fut.current = false; setKesz(true); } return uj; });
    } else {
      setElet(l => { const uj = l - 1; if (uj <= 0) { fut.current = false; setGameOver(true); } return uj; });
    }
  };

  const ujra = () => { fut.current = true; setPontok(0); setElet(3); setCellak(Array(9).fill(null)); setKesz(false); setGameOver(false); };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.25rem 1.5rem', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)', margin: '0 0 4px'}}>{feladat.cim}</p>
        <p style={{fontSize: '16px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>{feladat.kerdes}</p>
      </div>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-card)', borderRadius: '16px', padding: '0.75rem 1.25rem', border: '2px solid var(--border-color)'}}>
        <div style={{display: 'flex', gap: '4px'}}>{[0,1,2].map(i => <span key={i} style={{fontSize: '22px', opacity: i < elet ? 1 : 0.2}}>❤️</span>)}</div>
        <div style={{fontSize: '16px', fontWeight: '800', color: szin}}>⭐ {pontok} / 6</div>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem'}}>
        {cellak.map((c, i) => (
          <button key={i} onClick={() => kattint(i)} className="lk-btn"
            style={{height: '260px', borderRadius: '32px', border: `4px solid ${c ? (c.jo ? '#34a853' : '#ea4335') : '#e0e0e0'}`, background: c ? 'white' : 'var(--bg-card-alt)', fontSize: '48px', cursor: c ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            {c ? <KepVagyEmoji ertek={c.emoji} meret={155} /> : ''}
          </button>
        ))}
      </div>
      <p style={{fontSize: '14px', color: '#999', textAlign: 'center', fontWeight: '700'}}>👆 Kapd el gyorsan a jókat!</p>
      {kesz && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '22px', fontWeight: '900', color: '#1e7e34', margin: '0 0 1rem'}}>🎉 Nagyon ügyes voltál!</p>
          <button onClick={onKesz} className="lk-btn" style={{padding: '14px 36px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '17px', fontWeight: '800', cursor: 'pointer'}}>Tovább! 🚀</button>
        </div>
      )}
      {gameOver && !kesz && (
        <div style={{background: '#fce8e6', border: '2px solid #ea4335', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '20px', fontWeight: '900', color: '#c62828', margin: '0 0 1rem'}}>😔 Próbáld újra!</p>
          <button onClick={ujra} className="lk-btn" style={{padding: '14px 36px', background: 'linear-gradient(135deg, #f9ab00, #ff6d00)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '17px', fontWeight: '800', cursor: 'pointer'}}>🔄 Újra!</button>
        </div>
      )}
    </div>
  );
}

// ============ SORREND JÁTÉK (6. lecke) — a "megijedek" lépéssor gyakorlása ============
const SORREND_GOMBOK = [
  { id: 'allj', emoji: '🛑', felirat: 'Állj' },
  { id: 'menj', emoji: '🏃', felirat: 'Menj' },
  { id: 'mondd', emoji: '🗣️', felirat: 'Mondd el' },
];

function SorrendJatek({ feladat, onKesz, lecke }) {
  const szin = lecke?.szin || '#ffd93d';
  const [sorrend, setSorrend] = useState(['allj']);
  const [mutatas, setMutatas] = useState(true);
  const [mutatottIndex, setMutatottIndex] = useState(-1);
  const [sajatValasz, setSajatValasz] = useState([]);
  const [utolsoJoGomb, setUtolsoJoGomb] = useState(null);
  const [kor, setKor] = useState(1);
  const [hiba, setHiba] = useState(false);
  const [kesz, setKesz] = useState(false);
  const CELKOR = 4;

  useEffect(() => {
    setMutatas(true); setSajatValasz([]); setMutatottIndex(-1); setUtolsoJoGomb(null);
    const idok = [];
    sorrend.forEach((_, i) => {
      idok.push(setTimeout(() => setMutatottIndex(i), i * 800));
      idok.push(setTimeout(() => setMutatottIndex(-1), i * 800 + 550));
    });
    idok.push(setTimeout(() => setMutatas(false), sorrend.length * 800 + 300));
    return () => idok.forEach(clearTimeout);
  }, [sorrend]);

  const kattint = (id) => {
    if (mutatas || kesz) return;
    const idx = sajatValasz.length;
    if (sorrend[idx] !== id) {
      setHiba(true);
      setTimeout(() => { setHiba(false); setSajatValasz([]); }, 900);
      return;
    }
    setUtolsoJoGomb(id);
    setTimeout(() => setUtolsoJoGomb(null), 300);
    const ujValasz = [...sajatValasz, id];
    setSajatValasz(ujValasz);
    if (ujValasz.length === sorrend.length) {
      if (kor >= CELKOR) {
        setTimeout(() => setKesz(true), 400);
      } else {
        setTimeout(() => {
          setKor(k => k + 1);
          setSorrend(s => [...s, SORREND_GOMBOK[Math.floor(Math.random() * SORREND_GOMBOK.length)].id]);
        }, 800);
      }
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'felugrik 0.4s ease-out'}}>
      <div style={{background: 'var(--bg-card)', borderRadius: '24px', padding: '1.5rem 2rem', textAlign: 'center', border: '2px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)'}}>
        <p style={{fontSize: '30px', fontWeight: '900', color: 'var(--text-primary)', margin: '0 0 0.5rem'}}>{feladat.cim}</p>
        <p style={{fontSize: '18px', color: 'var(--text-secondary)', margin: 0, fontWeight: '700'}}>Figyeld meg a sorrendet, aztán ismételd!</p>
        <p style={{fontSize: '16px', color: szin, margin: '8px 0 0', fontWeight: '800'}}>Kör: {Math.min(kor, CELKOR)}/{CELKOR}</p>
      </div>
      {!kesz && (
        <div style={{background: hiba ? '#fce8e6' : 'white', border: `3px solid ${hiba ? '#ea4335' : `${szin}50`}`, borderRadius: '28px', padding: '2.5rem 2rem', textAlign: 'center'}}>
          <p style={{fontSize: '18px', fontWeight: '800', color: hiba ? '#c62828' : '#888', marginBottom: '2rem'}}>
            {hiba ? '😅 Nem az volt! Figyelj újra!' : mutatas ? '👀 Figyelj!' : '👆 Most te jössz!'}
          </p>
          <div style={{display: 'flex', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap'}}>
            {SORREND_GOMBOK.map((g) => {
              const aktivMutatas = mutatas && mutatottIndex >= 0 && sorrend[mutatottIndex] === g.id;
              const joLenyomas = utolsoJoGomb === g.id;
              return (
                <button key={g.id} onClick={() => kattint(g.id)} className="lk-btn"
                  style={{
                    width: '180px', height: '180px', borderRadius: '32px',
                    border: `5px solid ${aktivMutatas ? szin : joLenyomas ? '#34a853' : '#e0e0e0'}`,
                    background: aktivMutatas ? `${szin}35` : joLenyomas ? '#e8f5e9' : 'white',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    cursor: mutatas || kesz ? 'default' : 'pointer',
                    transform: (aktivMutatas || joLenyomas) ? 'scale(1.12)' : 'scale(1)', transition: 'all 0.2s',
                  }}>
                  <span style={{fontSize: '58px'}}>{g.emoji}</span>
                  <span style={{fontSize: '15px', fontWeight: '800', color: 'var(--text-secondary)'}}>{g.felirat}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {kesz && (
        <div style={{background: '#e8f5e9', border: '2px solid #34a853', borderRadius: '20px', padding: '1.5rem', textAlign: 'center'}}>
          <p style={{fontSize: '22px', fontWeight: '900', color: '#1e7e34', margin: '0 0 0.5rem'}}>🎉 Megtanultad a lépéseket!</p>
          <p style={{fontSize: '17px', fontWeight: '700', color: '#1e7e34', margin: '0 0 1rem'}}>🛑 Állj → 🏃 Menj a szülőhöz → 🗣️ Mondd el!</p>
          <button onClick={onKesz} className="lk-btn" style={{padding: '16px 40px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', border: 'none', borderRadius: '50px', fontSize: '18px', fontWeight: '800', cursor: 'pointer'}}>Tovább! 🚀</button>
        </div>
      )}
    </div>
  );
}

// ============ FŐ KOMPONENS ============
function Leckek({ felhasznalo }) {
  const [aktivLecke, setAktivLecke] = useState(null);
  const [aktivFeladat, setAktivFeladat] = useState(0);
  const [teljesitett, setTeljesitett] = useState([]);
  const [leckeKesz, setLeckeKesz] = useState(false);
  const [videoNezve, setVideoNezve] = useState(false);
  const [konyvOlvasva, setKonyvOlvasva] = useState(false);
  const { gyerekek, xpHozzaadasa } = useGyerekek();
  const aktivGyerek = gyerekek[0] || null;

  const visszaLeckekhez = () => { setAktivLecke(null); setAktivFeladat(0); setLeckeKesz(false); setVideoNezve(false); setKonyvOlvasva(false); };
  const leckeIndit = (lecke) => { setAktivLecke(lecke); setAktivFeladat(0); setLeckeKesz(false); setVideoNezve(false); setKonyvOlvasva(false); };
  const kovetkezoFeladat = () => {
    if (aktivFeladat + 1 < aktivLecke.feladatok.length) { setAktivFeladat(p => p + 1); }
    else {
      setLeckeKesz(true);
      if (!teljesitett.includes(aktivLecke.id)) {
        setTeljesitett(prev => [...prev, aktivLecke.id]);
        if (aktivGyerek) xpHozzaadasa(aktivGyerek.id, 30);
      }
    }
  };

  const vanVideo = aktivLecke && !!aktivLecke.video;
  const vanKonyv = aktivLecke && !!aktivLecke.konyv;
  const videoZarol = vanVideo && !videoNezve;
  const konyvZarol = vanKonyv && !konyvOlvasva;
  const haladásszazalek = Math.round((teljesitett.length / leckek.length) * 100);

  const renderFeladat = (f) => {
    const props = { feladat: f, onKesz: kovetkezoFeladat, lecke: aktivLecke };
    let tartalom = null;
    if (f.tipus === 'platformer') tartalom = <PlatformerJatek key={f.id} {...props}/>;
    else if (f.tipus === 'buborek') tartalom = <BuborekJatek key={f.id} {...props}/>;
    else if (f.tipus === 'kapdel') tartalom = <KapdElJatek key={f.id} {...props}/>;
    else if (f.tipus === 'villano') tartalom = <VillanoKartyak key={f.id} {...props}/>;
    else if (f.tipus === 'racs') tartalom = <RacsVadaszat key={f.id} {...props}/>;
    else if (f.tipus === 'sorrend') tartalom = <SorrendJatek key={f.id} {...props}/>;
    else if (f.tipus === 'uzenet_felismero') tartalom = <UzenetFelismeroFeladat key={f.id} {...props}/>;
    else if (f.tipus === 'szolo_szuloknek') tartalom = <SzolySzuloknek key={f.id} {...props}/>;
    else if (f.tipus === 'memoria') tartalom = <MemoriaJatek key={f.id} {...props}/>;
    else if (f.tipus === 'merleg') tartalom = <IgazHamisMerleg key={f.id} {...props}/>;
    else if (f.tipus === 'valaszd_ki') tartalom = <ValaszdKi key={f.id} {...props}/>;
    else if (f.tipus === 'mi_nem_illik') tartalom = <MiNemIllik key={f.id} {...props}/>;
    else if (f.tipus === 'parosito') tartalom = <ParositoJatek key={f.id} {...props}/>;
    else if (f.tipus === 'keresd_meg') tartalom = <KeresdMeg key={f.id} {...props}/>;
    else if (f.tipus === 'sorba_rendezes') tartalom = <SorbaRendezes key={f.id} {...props}/>;
    else if (f.tipus === 'kirakos') tartalom = <KirakosJatek key={f.id} {...props}/>;
    else if (f.tipus === 'szinezo') tartalom = <Szinezo key={f.id} {...props}/>;
    else if (f.tipus === 'osszekoto') tartalom = <OsszekotoPontok key={f.id} {...props}/>;
    else if (f.tipus === 'valogato') tartalom = <ValogatoFeladat key={f.id} {...props}/>;
    if (!tartalom) return null;
    return <FeladatFejlec leiras={f.leiras} szin={aktivLecke.szin}>{tartalom}</FeladatFejlec>;
  };

  if (!aktivLecke) {
    return (
      <div className="leckek-wrapper" style={{minHeight: 'calc(100vh - 70px)', background: 'var(--bg-page)', position: 'relative', overflow: 'hidden', padding: '4rem 2rem'}}>
        <style>{globalStyles}</style>
        <AnimaltHatter/>
        <div style={{position: 'relative', zIndex: 1}}>
          <div style={{textAlign: 'center', marginBottom: '3rem', animation: 'felugrik 0.5s ease-out'}}>
            <div style={{display: 'inline-flex', alignItems: 'center', gap: '10px', background: 'var(--bg-card-transparent)', backdropFilter: 'blur(10px)', border: '2px solid #ffd93d', borderRadius: '50px', padding: '10px 24px', fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '1.25rem'}}>
              <span style={{fontSize: '24px', animation: 'ugorj 2s ease-in-out infinite'}}>🤖</span> Cyber Robi kalandjai
            </div>
            <h1 style={{fontSize: '46px', fontWeight: '900', color: 'var(--text-primary)', marginBottom: '1rem'}}>
              Fedezd fel a <span style={{background: 'linear-gradient(135deg, #ff6b9d, #4d96ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'}}>digitális világot!</span>
            </h1>
            <p style={{fontSize: '19px', color: 'var(--text-secondary)', maxWidth: '480px', margin: '0 auto 2rem', fontWeight: '700', lineHeight: 1.6}}>6 kaland Cyber Robival! 🛡️</p>
            <div style={{background: 'var(--bg-card-transparent)', backdropFilter: 'blur(10px)', borderRadius: '24px', padding: '1.25rem 2rem', maxWidth: '380px', margin: '0 auto', boxShadow: '0 8px 28px rgba(0,0,0,0.08)'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                <span style={{fontSize: '15px', fontWeight: '800', color: 'var(--text-secondary)'}}>Haladás</span>
                <span style={{fontSize: '15px', fontWeight: '900', color: '#4d96ff'}}>{teljesitett.length}/{leckek.length}</span>
              </div>
              <div style={{background: 'var(--border-color)', borderRadius: '10px', height: '14px', overflow: 'hidden'}}>
                <div style={{background: 'linear-gradient(90deg, #ff6b9d, #4d96ff)', width: `${haladásszazalek}%`, height: '14px', borderRadius: '10px', transition: 'width 0.6s'}}/>
              </div>
            </div>
          </div>

          <div style={{maxWidth: '1280px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem'}}>
            {leckek.map((lecke, i) => {
              const teljesitettE = teljesitett.includes(lecke.id);
              return (
                <div key={lecke.id} onClick={() => leckeIndit(lecke)} className="lk-kartya"
                  style={{background: 'var(--bg-card-transparent)', backdropFilter: 'blur(10px)', borderRadius: '26px', padding: '1.75rem', cursor: 'pointer', border: `3px solid ${teljesitettE ? lecke.szin : 'var(--border-color-strong)'}`, boxShadow: teljesitettE ? `0 8px 28px ${lecke.szin}30` : '0 4px 20px rgba(0,0,0,0.08)', position: 'relative', overflow: 'hidden'}}>
                  <div style={{position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', borderRadius: '50%', background: `${lecke.szin}12`}}/>
                  {teljesitettE && <div style={{position: 'absolute', top: '14px', right: '14px', background: 'linear-gradient(135deg, #34a853, #66bb6a)', color: 'white', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '900', boxShadow: '0 4px 12px rgba(52,168,83,0.4)'}}>✓</div>}
                  <div style={{width: '72px', height: '72px', borderRadius: '20px', background: `linear-gradient(135deg, ${lecke.szin}30, ${lecke.szin}15)`, border: `2px solid ${lecke.szin}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '38px', marginBottom: '1rem', animation: 'ugorj 3s ease-in-out infinite', animationDelay: `${i * 0.4}s`}}>
                    {lecke.ikon}
                  </div>
                  <div style={{fontSize: '12px', color: '#bbb', fontWeight: '800', marginBottom: '4px', letterSpacing: '1px', textTransform: 'uppercase'}}>{i + 1}. kaland</div>
                  <div style={{fontSize: '22px', fontWeight: '900', color: 'var(--text-primary)', marginBottom: '0.5rem'}}>{lecke.cim}</div>
                  <div style={{fontSize: '14px', color: 'var(--text-muted)', fontWeight: '700', marginBottom: '1rem', lineHeight: 1.5}}>{lecke.leiras}</div>
                  <div style={{display: 'flex', gap: '6px', marginBottom: '1rem', flexWrap: 'wrap'}}>
                    {lecke.video && <div style={{fontSize: '12px', background: '#e3f2fd', color: '#1565c0', borderRadius: '8px', padding: '3px 10px', fontWeight: '800'}}>🎬 Videó</div>}
                    {lecke.konyv && <div style={{fontSize: '12px', background: '#f3e5f5', color: '#6a1b9a', borderRadius: '8px', padding: '3px 10px', fontWeight: '800'}}>📖 Mese</div>}
                    <div style={{fontSize: '12px', background: 'var(--bg-card-alt)', color: 'var(--text-muted)', borderRadius: '8px', padding: '3px 10px', fontWeight: '800'}}>{lecke.feladatok.length} játék</div>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <div style={{background: `${lecke.szin}20`, color: lecke.szin, fontSize: '14px', fontWeight: '900', padding: '6px 16px', borderRadius: '50px', border: `1.5px solid ${lecke.szin}40`}}>
                      {teljesitettE ? '✓ Kész! 🌟' : '+30 XP ⚡'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const feladat = aktivLecke.feladatok[aktivFeladat];

  return (
    <div className="leckek-wrapper" style={{minHeight: 'calc(100vh - 70px)', background: 'var(--bg-page)', position: 'relative', overflow: 'hidden', padding: '1.5rem 1.5rem 3rem'}}>
      <style>{globalStyles}</style>
      <AnimaltHatter/>
      <div style={{position: 'relative', zIndex: 1}}>
        <div style={{maxWidth: '1280px', margin: '0 auto 1.5rem', background: 'var(--bg-card-transparent)', backdropFilter: 'blur(10px)', borderRadius: '20px', padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: `2px solid ${aktivLecke.szin}30`}}>
          <button onClick={visszaLeckekhez} className="lk-btn"
            style={{background: `${aktivLecke.szin}18`, border: `2px solid ${aktivLecke.szin}50`, borderRadius: '12px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: aktivLecke.szin, fontWeight: '800'}}>
            ← Vissza
          </button>
          <span style={{fontSize: '24px'}}>{aktivLecke.ikon}</span>
          <span style={{fontSize: '16px', fontWeight: '900', color: 'var(--text-primary)'}}>{aktivLecke.cim}</span>
          <div style={{flex: 1}}/>
          <div style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
            {vanVideo && <div style={{width: '22px', height: '7px', borderRadius: '4px', background: videoNezve ? aktivLecke.szin : `${aktivLecke.szin}30`, transition: 'background 0.4s'}}/>}
            {vanKonyv && <div style={{width: '22px', height: '7px', borderRadius: '4px', background: konyvOlvasva ? aktivLecke.szin : `${aktivLecke.szin}30`, transition: 'background 0.4s'}}/>}
            {aktivLecke.feladatok.map((_, i) => (
              <div key={i} style={{width: '22px', height: '7px', borderRadius: '4px', background: (!videoZarol && !konyvZarol) ? (i < aktivFeladat ? aktivLecke.szin : i === aktivFeladat ? `${aktivLecke.szin}80` : '#e0e0e0') : '#e0e0e0', transition: 'background 0.3s'}}/>
            ))}
          </div>
          <div style={{fontSize: '13px', color: '#aaa', fontWeight: '800', minWidth: '60px', textAlign: 'right'}}>
            {videoZarol ? '🎬 Videó' : konyvZarol ? '📖 Mese' : `${aktivFeladat + 1}/${aktivLecke.feladatok.length}`}
          </div>
        </div>

        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          {videoZarol ? (
            <VideoLejatszo videoUrl={aktivLecke.video} onVegig={() => setVideoNezve(true)} lecke={aktivLecke}/>
          ) : konyvZarol ? (
            <KonyvOlvaso konyv={aktivLecke.konyv} lecke={aktivLecke} onKesz={() => setKonyvOlvasva(true)}/>
          ) : !leckeKesz ? (
            <div style={{display: 'flex', gap: '2rem', alignItems: 'flex-start'}}>
              <div style={{width: '150px', flexShrink: 0, background: 'var(--bg-card-transparent)', backdropFilter: 'blur(10px)', borderRadius: '22px', padding: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'sticky', top: '80px', boxShadow: `0 6px 20px ${aktivLecke.szin}20`, border: `2px solid ${aktivLecke.szin}25`}}>
                <img src="/cyber_robi.png" alt="Cyber Robi" style={{width: '130px', height: 'auto', animation: 'pulzal 3s ease-in-out infinite', filter: `drop-shadow(0 6px 16px ${aktivLecke.szin}40)`}}/>
              </div>
              <div style={{flex: 1, animation: 'felugrik 0.4s ease-out'}}>
                {renderFeladat(feladat)}
              </div>
            </div>
          ) : (
            <div style={{background: 'var(--bg-card-transparent)', border: `3px solid ${aktivLecke.szin}`, borderRadius: '32px', padding: '4rem 2.5rem', textAlign: 'center', boxShadow: `0 20px 60px ${aktivLecke.szin}25`, animation: 'felugrik 0.5s cubic-bezier(0.34,1.56,0.64,1)', position: 'relative', overflow: 'hidden'}}>
              <KonfettiHatter/>
              <div style={{position: 'relative', zIndex: 1}}>
                <div style={{fontSize: '96px', marginBottom: '1rem', animation: 'ugorj 0.8s ease-in-out infinite'}}>🎉</div>
                <h2 style={{fontSize: '34px', fontWeight: '900', color: aktivLecke.szin, marginBottom: '0.5rem'}}>Szuper! Kész a kaland!</h2>
                <p style={{color: 'var(--text-muted)', fontSize: '18px', marginBottom: '0.5rem', fontWeight: '800'}}>Minden játékot megnyertél!</p>
                <p style={{color: '#4d96ff', fontWeight: '900', fontSize: '26px', marginBottom: '1.5rem'}}>+30 XP! ⚡</p>
                <div style={{background: `${aktivLecke.szin}12`, border: `2px solid ${aktivLecke.szin}40`, borderRadius: '20px', padding: '1.25rem 2rem', marginBottom: '2rem', maxWidth: '500px', margin: '0 auto 2rem'}}>
                  <p style={{fontSize: '18px', fontWeight: '800', color: aktivLecke.szin, margin: 0, lineHeight: 1.6}}>
                    🌟 Furcsa/ijesztő dolog → szólj a szülőknek! 💕
                  </p>
                </div>
                <button onClick={visszaLeckekhez} className="lk-btn"
                  style={{padding: '18px 52px', background: `linear-gradient(135deg, ${aktivLecke.szin}, ${aktivLecke.szin}cc)`, color: 'white', border: 'none', borderRadius: '50px', fontSize: '19px', fontWeight: '900', cursor: 'pointer', boxShadow: `0 10px 28px ${aktivLecke.szin}45`}}>
                  📚 Vissza a kalandokhoz
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Leckek;